import React, { useState, useEffect, useRef, useCallback } from 'react';
import StatusNotification from '../components/StatusNotification';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  CircularProgress,
  Alert,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Chip,
  Tabs,
  Tab,
  Divider,
  Stack,
  ListItemText,
  Menu,
  ListItemIcon,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import SettingsIcon from '@mui/icons-material/Settings';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PublishIcon from '@mui/icons-material/Publish';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import MicIcon from '@mui/icons-material/Mic';
import PaymentsIcon from '@mui/icons-material/Payments';
import LinkIcon from '@mui/icons-material/Link';
import SecurityIcon from '@mui/icons-material/Security';
import SmsIcon from '@mui/icons-material/Sms';
import CallIcon from '@mui/icons-material/Call';
import StorefrontIcon from '@mui/icons-material/Storefront';
import HubIcon from '@mui/icons-material/Hub';
import WidgetsOutlinedIcon from '@mui/icons-material/WidgetsOutlined';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { API_BASE_URL } from '../config/api';
import ChatPreview from '../components/ChatPreview';
import { createInitialChatWidgetForm, resolveAgentIdentityText, resolveChatWidgetConfig } from '../utils/chatWidgetConfig';
import { maskSecret } from '../utils/maskSecret';

const WIDGET_FONT_OPTIONS = [
  { label: 'System default', value: 'Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' },
  { label: 'Arial', value: 'Arial, sans-serif' },
  { label: 'Helvetica', value: 'Helvetica, Arial, sans-serif' },
  { label: 'Verdana', value: 'Verdana, Geneva, sans-serif' },
  { label: 'Tahoma', value: 'Tahoma, Geneva, sans-serif' },
  { label: 'Trebuchet MS', value: '"Trebuchet MS", sans-serif' },
  { label: 'Georgia', value: 'Georgia, serif' },
  { label: 'Times New Roman', value: '"Times New Roman", Times, serif' },
  { label: 'Courier New', value: '"Courier New", Courier, monospace' },
];
const BOT_LANGUAGE_OPTIONS = [
  ['en', 'English'], ['hi', 'Hindi'], ['hi-en', 'Hinglish'], ['bn', 'Bengali'],
  ['mr', 'Marathi'], ['gu', 'Gujarati'], ['ta', 'Tamil'], ['te', 'Telugu'],
  ['kn', 'Kannada'], ['ml', 'Malayalam'], ['pa', 'Punjabi'], ['ur', 'Urdu'],
];

/** Mask stored WhatsApp credentials while retaining a recognizable prefix and suffix. */
const maskIntegrationCredential = (value = '') => {
  const credential = String(value || '');
  if (!credential) return '';
  if (credential.length <= 8) return '*'.repeat(credential.length);
  return `${credential.slice(0, 4)}${'*'.repeat(credential.length - 8)}${credential.slice(-4)}`;
};

/** Purpose: Build editable environment settings from stored config and bot defaults. Why: Missing fields need stable defaults while preserving saved provider choices. */
const createDefaultEnvForm = (config = {}, botName = '') => ({
  twilioPhoneNumber: config.twilioPhoneNumber || '',
  aiPrompt: config.aiPrompt || '',
  voiceSettings: {
    provider: config.voiceSettings?.provider || 'elevenlabs',
    voiceId: config.voiceSettings?.voiceId || 'default',
    language: config.voiceSettings?.language || 'en-US',
    speed: config.voiceSettings?.speed || 1.0,
  },
  callFlow: {
    greeting: config.callFlow?.greeting || '',
    initialFlowId: config.callFlow?.initialFlowId || '',
  },
  settings: {
    intentConfidenceThreshold: Number.isFinite(Number(config.settings?.intentConfidenceThreshold))
      ? Number(config.settings.intentConfidenceThreshold)
      : 56,
    intentIgnoreThreshold: Number.isFinite(Number(config.settings?.intentIgnoreThreshold))
      ? Number(config.settings.intentIgnoreThreshold)
      : 30,
  },
  chatWidget: createInitialChatWidgetForm(config.chatWidget || {}, botName),
  integrations: {
    whatsapp: {
      enabled: config.integrations?.whatsapp?.enabled || false,
      provider: config.integrations?.whatsapp?.provider || 'meta',
      accessToken: '',
      accessTokenConfigured: Boolean(config.integrations?.whatsapp?.accessTokenConfigured || config.integrations?.whatsapp?.accessToken),
      phoneNumberId: config.integrations?.whatsapp?.phoneNumberId || '',
      senders: Array.isArray(config.integrations?.whatsapp?.senders) ? config.integrations.whatsapp.senders.map((sender) => ({
      senderId: sender.senderId || '', phoneNumberId: sender.phoneNumberId || '',
        displayPhoneNumber: sender.displayPhoneNumber || '', enabled: sender.enabled !== false,
      })) : [],
      // App ID is a non-secret setting; keep it in the editable form so refreshes
      // and unrelated integration saves do not blank the persisted value.
      appId: config.integrations?.whatsapp?.appId || '',
      wabaId: config.integrations?.whatsapp?.wabaId || '',
      verifyToken: '',
      verifyTokenConfigured: Boolean(config.integrations?.whatsapp?.verifyTokenConfigured || config.integrations?.whatsapp?.verifyToken),
      webhookUrl: config.integrations?.whatsapp?.webhookUrl || '',
      graphVersion: config.integrations?.whatsapp?.graphVersion || 'v26.0',
      sessionTimeoutMinutes: Number(config.integrations?.whatsapp?.sessionTimeoutMinutes) || 30,
    },
    voice: {
      enabled: config.integrations?.voice?.enabled || false,
      provider: config.integrations?.voice?.provider || 'genesis',
      apiKey: config.integrations?.voice?.apiKey || '',
      projectId: config.integrations?.voice?.projectId || '',
      webhookUrl: config.integrations?.voice?.webhookUrl || '',
      sttProvider: config.integrations?.voice?.sttProvider || 'whisper',
      ttsProvider: config.integrations?.voice?.ttsProvider || 'elevenlabs',
    },
    payments: {
      enabled: config.integrations?.payments?.enabled || false,
      provider: config.integrations?.payments?.provider || 'razorpay',
      keyId: config.integrations?.payments?.keyId || '',
      keySecret: config.integrations?.payments?.keySecret || '',
      webhookSecret: config.integrations?.payments?.webhookSecret || '',
      currency: config.integrations?.payments?.currency || 'INR',
    },
    common: {
      crmWebhookUrl: config.integrations?.common?.crmWebhookUrl || '',
      analyticsWebhookUrl: config.integrations?.common?.analyticsWebhookUrl || '',
      automationWebhookUrl: config.integrations?.common?.automationWebhookUrl || '',
      supportEmail: config.integrations?.common?.supportEmail || '',
      notes: config.integrations?.common?.notes || '',
    },
    providers: {
      twilio: {
        enabled: config.integrations?.providers?.twilio?.enabled || false,
        accountSid: config.integrations?.providers?.twilio?.accountSid || '',
        authToken: config.integrations?.providers?.twilio?.authToken || '',
        phoneNumber: config.integrations?.providers?.twilio?.phoneNumber || '',
        webhookUrl: config.integrations?.providers?.twilio?.webhookUrl || '',
      },
      gupshup: {
        enabled: config.integrations?.providers?.gupshup?.enabled || false,
        setupMode: config.integrations?.providers?.gupshup?.setupMode || 'partner',
        accountName: config.integrations?.providers?.gupshup?.accountName || '',
        appName: config.integrations?.providers?.gupshup?.appName || '',
        apiKey: config.integrations?.providers?.gupshup?.apiKey || '',
        appId: config.integrations?.providers?.gupshup?.appId || '',
        partnerToken: config.integrations?.providers?.gupshup?.partnerToken || '',
        appToken: config.integrations?.providers?.gupshup?.appToken || config.integrations?.providers?.gupshup?.apiKey || '',
        phoneNumber: config.integrations?.providers?.gupshup?.phoneNumber || '',
        wabaId: config.integrations?.providers?.gupshup?.wabaId || '',
        apiVersion: config.integrations?.providers?.gupshup?.apiVersion || 'v3',
        webhookSecret: config.integrations?.providers?.gupshup?.webhookSecret || '',
        webhookVerifyToken: config.integrations?.providers?.gupshup?.webhookVerifyToken || '',
        subscriptionId: config.integrations?.providers?.gupshup?.subscriptionId || '',
        onboardingLink: config.integrations?.providers?.gupshup?.onboardingLink || '',
        channel: config.integrations?.providers?.gupshup?.channel || 'whatsapp',
        webhookUrl: config.integrations?.providers?.gupshup?.webhookUrl || '',
      },
      msg91: {
        enabled: config.integrations?.providers?.msg91?.enabled || false,
        authKey: config.integrations?.providers?.msg91?.authKey || '',
        flowId: config.integrations?.providers?.msg91?.flowId || '',
        senderId: config.integrations?.providers?.msg91?.senderId || '',
        webhookUrl: config.integrations?.providers?.msg91?.webhookUrl || '',
      },
      interakt: {
        enabled: config.integrations?.providers?.interakt?.enabled || false,
        apiKey: config.integrations?.providers?.interakt?.apiKey || '',
        workspaceId: config.integrations?.providers?.interakt?.workspaceId || '',
        phoneNumber: config.integrations?.providers?.interakt?.phoneNumber || '',
        webhookUrl: config.integrations?.providers?.interakt?.webhookUrl || '',
      },
      exotel: {
        enabled: config.integrations?.providers?.exotel?.enabled || false,
        sid: config.integrations?.providers?.exotel?.sid || '',
        apiKey: config.integrations?.providers?.exotel?.apiKey || '',
        apiToken: config.integrations?.providers?.exotel?.apiToken || '',
        subdomain: config.integrations?.providers?.exotel?.subdomain || '',
      },
    },
  },
});

/** Purpose: manage workspace preferences, integrations, publishing, and access. Why: bot-wide configuration belongs in one permission-aware settings surface. */
export default function Settings() {
  const { apiKey, saveApiKey, isAdmin, isBotAdmin, selectedBot, selectedEnv, selectedBotDetails } = useAuth();
  const selectedBotName = selectedBotDetails?.botName || selectedBot || 'Bot Preview';
  const [apiKeyInput, setApiKeyInput] = useState(apiKey || '');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [message, setMessage] = useState(null);
  const [showApiKey, setShowApiKey] = useState(false);
  const [settingsTab, setSettingsTab] = useState(0);
  const [apiKeys, setApiKeys] = useState([]);
  const [generateApiKeyDialogOpen, setGenerateApiKeyDialogOpen] = useState(false);
  const [newApiKeyName, setNewApiKeyName] = useState('');
  
  // Invite member dialog
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteScope, setInviteScope] = useState('team');
  const [showInvitePassword, setShowInvitePassword] = useState(false);
  const [inviteForm, setInviteForm] = useState({
    email: '',
    name: '',
    password: '',
    workspaceRoles: ['developer'],
    workspaceRole: 'developer',
    role: 'developer',
    botIds: [],
    environmentsAllowed: ['dev'],
    accessLevel: 'developer',
  });
  const [bots, setBots] = useState([]);
  const [inviting, setInviting] = useState(false);
  const [accessMembers, setAccessMembers] = useState([]);
  const [accessLoading, setAccessLoading] = useState(false);
  const [actionAnchorEl, setActionAnchorEl] = useState(null);
  const [activeActionMember, setActiveActionMember] = useState(null);
  const [editRoleDialogOpen, setEditRoleDialogOpen] = useState(false);
  const [editRoleMember, setEditRoleMember] = useState(null);
  const [editRoleValues, setEditRoleValues] = useState(['developer']);
  const [editRoleEnvs, setEditRoleEnvs] = useState(['dev']);
  const [editRoleSaving, setEditRoleSaving] = useState(false);
  const [deleteAccessDialogOpen, setDeleteAccessDialogOpen] = useState(false);
  const [deleteAccessMember, setDeleteAccessMember] = useState(null);
  const [deleteAccessSaving, setDeleteAccessSaving] = useState(false);
  
  // Environment configuration
  const [envConfig, setEnvConfig] = useState(null);
  const [envLoading, setEnvLoading] = useState(false);
  const [envSaving, setEnvSaving] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const avatarFileInputRef = useRef(null);
  const [flows, setFlows] = useState([]);
  const [flowsLoading, setFlowsLoading] = useState(false);
  const [envForm, setEnvForm] = useState({
    ...createDefaultEnvForm({}, selectedBotName),
  });
  const [whatsappCredentialDrafts, setWhatsappCredentialDrafts] = useState({ accessToken: '', verifyToken: '' });
  const [whatsappCredentialEditing, setWhatsappCredentialEditing] = useState({ accessToken: false, verifyToken: false });
  const [previewHomeCommand, setPreviewHomeCommand] = useState('home');
  const [selectedIntegrationCard, setSelectedIntegrationCard] = useState('whatsapp');
  const [gupshupLoginForm, setGupshupLoginForm] = useState({ email: '', secret: '' });
  const [gupshupApps, setGupshupApps] = useState([]);
  const [gupshupBusy, setGupshupBusy] = useState(false);
  const [genesysBusy, setGenesysBusy] = useState(false);
  const [genesysForm, setGenesysForm] = useState({ enabled: false, clientId: '', clientSecret: '', webhookSecret: '', apiBaseUrl: '', loginBaseUrl: '', createConversationPath: '', sendMessagePath: '', sendAttachmentPath: '', transferPath: '', statusPath: '' });
  const [aiProviderBusy, setAiProviderBusy] = useState(false);
  const [aiProviderForm, setAiProviderForm] = useState({
    provider: 'openai', enabled: false, apiKey: '', apiKeyHint: '', model: 'gpt-4o-mini',
    baseUrl: 'https://api.openai.com/v1', organization: '', credentialsConfigured: false,
  });
  const [translationProviderBusy, setTranslationProviderBusy] = useState(false);
  const [translationProviderForm, setTranslationProviderForm] = useState({ provider: 'google', enabled: false, apiKey: '', apiKeyHint: '', endpoint: 'https://translation.googleapis.com/language/translate/v2', region: '', credentialsConfigured: false });
  const [botLanguagesBusy, setBotLanguagesBusy] = useState(false);
  const [botLanguages, setBotLanguages] = useState({ defaultLanguage: 'en', enabledLanguages: ['en'], entries: [] });
  const [allowedBotLanguages, setAllowedBotLanguages] = useState(['en']);

  const settingsCardSx = {
    p: 2,
    borderRadius: 4,
    border: '1px solid',
    borderColor: 'divider',
    /** Purpose: Choose a theme-aware panel background. Why: Settings surfaces should retain contrast in either theme. */
    background: (theme) => theme.palette.mode === 'dark'
      ? 'linear-gradient(180deg, rgba(15,23,42,0.9) 0%, rgba(15,23,42,0.72) 100%)'
      : 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
    boxShadow: '0 12px 30px rgba(15,23,42,0.08)',
  };
  const chatWidgetCardSx = {
    ...settingsCardSx,
    p: 1.75,
    borderRadius: 3.5,
  };
  const previewCardSx = {
    ...settingsCardSx,
    p: 1.75,
    borderRadius: 4,
    display: 'flex',
    flexDirection: 'column',
    gap: 1.5,
  };

  const widgetSizeCards = [
    { key: 'small', label: 'Small', scale: 0.78, width: 340, height: 520 },
    { key: 'medium', label: 'Medium', scale: 0.9, width: 404, height: 640 },
    { key: 'large', label: 'Large', scale: 1.02, width: 480, height: 760 },
  ];
  const resolvedChatWidgetPreview = resolveChatWidgetConfig(envForm.chatWidget, selectedBotName);

  /** Purpose: Show only a short prefix and suffix of a configured credential. Why: Operators can identify a key without exposing its secret. */
  const maskApiKey = maskSecret;

  /** Purpose: Merge edits into one integration section. Why: Nested settings updates must preserve sibling provider configuration. */
  const updateIntegrationSection = (section, patch) => {
    setEnvForm((current) => ({
      ...current,
      integrations: {
        ...current.integrations,
        [section]: {
          ...current.integrations[section],
          ...patch,
        },
      },
    }));
  };

  const addWhatsAppSender = () => {
    const whatsapp = envForm.integrations.whatsapp;
    let senders = Array.isArray(whatsapp.senders) ? whatsapp.senders : [];
    // Preserve the existing legacy sender as a selectable entry before adding
    // another number; this avoids silently changing the old default route.
    if (!senders.length && whatsapp.phoneNumberId) senders = [{
      senderId: 'default', phoneNumberId: whatsapp.phoneNumberId,
      displayPhoneNumber: '', enabled: true,
    }];
    const senderId = `sender_${Date.now().toString(36)}`;
    updateIntegrationSection('whatsapp', { senders: [...senders, { senderId, phoneNumberId: '', displayPhoneNumber: '', enabled: true }] });
  };

  const updateWhatsAppSender = (index, patch) => updateIntegrationSection('whatsapp', {
    senders: (envForm.integrations.whatsapp.senders || []).map((sender, position) => position === index ? { ...sender, ...patch } : sender),
  });

  const removeWhatsAppSender = (index) => updateIntegrationSection('whatsapp', {
    senders: (envForm.integrations.whatsapp.senders || []).filter((_, position) => position !== index),
  });

  /** Purpose: Replace only explicitly edited WhatsApp credentials. Why: Saving unrelated settings must preserve the configured secrets. */
  const getIntegrationsForSave = () => ({
    ...envForm.integrations,
    whatsapp: {
      ...envForm.integrations.whatsapp,
      accessTokenConfigured: undefined,
      verifyTokenConfigured: undefined,
      ...(whatsappCredentialDrafts.accessToken !== '' ? { accessToken: whatsappCredentialDrafts.accessToken } : {}),
      ...(whatsappCredentialDrafts.verifyToken !== '' ? { verifyToken: whatsappCredentialDrafts.verifyToken } : {}),
    },
  });

  const resetWhatsAppCredentialDrafts = () => {
    setWhatsappCredentialDrafts({ accessToken: '', verifyToken: '' });
    setWhatsappCredentialEditing({ accessToken: false, verifyToken: false });
  };

  /** Purpose: Merge edits into one provider entry. Why: Switching provider settings should not erase other configured integrations. */
  const updateProviderSection = (provider, patch) => {
    setEnvForm((current) => ({
      ...current,
      integrations: {
        ...current.integrations,
        providers: {
          ...(current.integrations.providers || {}),
          [provider]: {
            ...(current.integrations.providers?.[provider] || {}),
            ...patch,
          },
        },
      },
    }));
  };

  /** Purpose: Merge chat widget changes into the current environment form. Why: Visual and identity settings share one persisted configuration object. */
  const updateChatWidget = (patch) => {
    setEnvForm((current) => ({
      ...current,
      chatWidget: {
        ...current.chatWidget,
        ...patch,
      },
    }));
  };

  /** Purpose: Validate and upload a widget avatar image. Why: Only supported image files of an acceptable size should enter the environment configuration. */
  const handleAvatarUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type || !file.type.startsWith('image/')) {
      setMessage({
        type: 'error',
        text: 'Please select a valid image file (PNG, JPG, SVG, WebP, GIF).',
      });
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setMessage({
        type: 'error',
        text: 'Avatar image must be 10 MB or smaller.',
      });
      return;
    }

    const formData = new FormData();
    formData.append('avatar', file);
    formData.append('media', file);
    formData.append('file', file);

    setAvatarUploading(true);
    try {
      let uploadedUrl = '';
      try {
        const response = await axios.post('/api/v1/environments/avatar', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        uploadedUrl = response.data?.avatarUrl || response.data?.mediaUrl || '';
      } catch (err) {
        // Fallback to flow media endpoint if needed
        const flowResponse = await axios.post('/api/v1/flows/media', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        uploadedUrl = flowResponse.data?.mediaUrl || flowResponse.data?.avatarUrl || '';
      }

      if (uploadedUrl) {
        updateChatWidget({ avatarUrl: uploadedUrl });
        setMessage({
          type: 'success',
          text: 'Avatar image uploaded successfully! Save changes to keep settings.',
        });
      } else {
        throw new Error('No image URL returned from upload.');
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || error.message || 'Failed to upload avatar image.',
      });
    } finally {
      setAvatarUploading(false);
      if (event.target) {
        event.target.value = '';
      }
    }
  };

  const integrationCards = [
    {
      key: 'ai_provider',
      title: 'Dynamic Chat AI',
      subtitle: 'Tenant-scoped OpenAI-compatible provider',
      icon: <HubIcon color="secondary" />,
      enabled: Boolean(aiProviderForm.enabled && aiProviderForm.credentialsConfigured),
    },
    {
      key: 'translation_provider',
      title: 'Automatic Translation',
      subtitle: 'Google, Azure, or DeepL for localization drafts',
      icon: <IntegrationInstructionsIcon color="primary" />,
      enabled: Boolean(translationProviderForm.enabled && translationProviderForm.credentialsConfigured),
    },
    {
      key: 'genesys',
      title: 'Genesys Cloud',
      subtitle: 'Live-agent routing, messaging and status sync',
      icon: <HubIcon color="primary" />,
      enabled: Boolean(genesysForm.enabled),
    },
    {
      key: 'whatsapp',
      title: 'WhatsApp Cloud API',
      subtitle: 'Meta, phone number, verify token, and webhook',
      icon: <WhatsAppIcon color="success" />,
      enabled: Boolean(envForm.integrations.whatsapp.enabled),
    },
    {
      key: 'voice',
      title: 'Voice & Calling',
      subtitle: 'Genesis, Twilio, Exotel, STT/TTS setup',
      icon: <MicIcon color="secondary" />,
      enabled: Boolean(envForm.integrations.voice.enabled),
    },
    {
      key: 'payments',
      title: 'Payments',
      subtitle: 'Razorpay, Cashfree, Stripe credentials',
      icon: <PaymentsIcon color="warning" />,
      enabled: Boolean(envForm.integrations.payments.enabled),
    },
    {
      key: 'common',
      title: 'Common Webhooks',
      subtitle: 'CRM, analytics, automation, and support',
      icon: <LinkIcon color="info" />,
      enabled: Boolean(
        envForm.integrations.common.crmWebhookUrl ||
        envForm.integrations.common.analyticsWebhookUrl ||
        envForm.integrations.common.automationWebhookUrl ||
        envForm.integrations.common.supportEmail
      ),
    },
    {
      key: 'twilio',
      title: 'Twilio',
      subtitle: 'Messaging and voice account settings',
      icon: <SmsIcon color="primary" />,
      enabled: Boolean(envForm.integrations.providers?.twilio?.enabled),
    },
    {
      key: 'gupshup',
      title: 'Gupshup',
      subtitle: 'WhatsApp partner app and API setup',
      icon: <HubIcon color="success" />,
      enabled: Boolean(envForm.integrations.providers?.gupshup?.enabled),
    },
    {
      key: 'msg91',
      title: 'MSG91',
      subtitle: 'Campaign and template messaging',
      icon: <StorefrontIcon color="warning" />,
      enabled: Boolean(envForm.integrations.providers?.msg91?.enabled),
    },
    {
      key: 'interakt',
      title: 'Interakt',
      subtitle: 'WhatsApp commerce and support workflows',
      icon: <WhatsAppIcon color="success" />,
      enabled: Boolean(envForm.integrations.providers?.interakt?.enabled),
    },
    {
      key: 'exotel',
      title: 'Exotel',
      subtitle: 'India-ready telephony and IVR setup',
      icon: <CallIcon color="secondary" />,
      enabled: Boolean(envForm.integrations.providers?.exotel?.enabled),
    },
  ];

  /** Purpose: Load tenant-scoped AI provider settings without requesting the secret again. Why: Credential reads should remain masked while model settings stay editable. */
  const fetchAiProvider = useCallback(async () => {
    if (!selectedBot) return;
    try {
      const response = await axios.get('/api/v1/ai-providers', { params: { botId: selectedBot, env: selectedEnv } });
      setAiProviderForm((current) => ({
        ...current,
        ...response.data.provider,
        apiKey: '',
        provider: response.data.provider?.provider || 'openai',
        model: response.data.provider?.model || 'gpt-4o-mini',
        baseUrl: response.data.provider?.baseUrl || 'https://api.openai.com/v1',
      }));
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Could not load AI provider configuration' });
    }
  }, [selectedBot, selectedEnv]);

  /** Purpose: Persist the selected AI provider, model, endpoint, and replacement key. Why: Each bot/environment can configure its own compatible provider. */
  const handleSaveAiProvider = async () => {
    setAiProviderBusy(true);
    setMessage(null);
    try {
      const response = await axios.put('/api/v1/ai-providers', {
        botId: selectedBot, env: selectedEnv, provider: aiProviderForm.provider,
        enabled: Boolean(aiProviderForm.enabled), apiKey: aiProviderForm.apiKey,
        model: aiProviderForm.model, baseUrl: aiProviderForm.baseUrl,
        organization: aiProviderForm.organization || '',
      });
      setAiProviderForm((current) => ({ ...current, ...response.data.provider, apiKey: '' }));
      setMessage({ type: 'success', text: 'Tenant AI provider configuration saved securely.' });
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || error.response?.data?.error || 'Could not save AI provider configuration' });
    } finally {
      setAiProviderBusy(false);
    }
  };

  /** Purpose: Load translation settings while keeping the saved credential hidden. Why: Editing provider metadata must not accidentally clear an existing key. */
  const fetchTranslationProvider = useCallback(async () => {
    if (!selectedBot) return;
    try {
      const response = await axios.get('/api/v1/translation-providers', { params: { botId: selectedBot, env: selectedEnv } });
      setTranslationProviderForm((current) => ({ ...current, ...response.data.translationProvider, apiKey: '' }));
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Could not load translation provider' });
    }
  }, [selectedBot, selectedEnv]);

  /** Purpose: Persist translation provider options and any replacement credential. Why: Localization behavior depends on environment-specific provider setup. */
  const handleSaveTranslationProvider = async () => {
    setTranslationProviderBusy(true); setMessage(null);
    try {
      const response = await axios.put('/api/v1/translation-providers', { botId: selectedBot, env: selectedEnv, provider: translationProviderForm.provider, enabled: Boolean(translationProviderForm.enabled), apiKey: translationProviderForm.apiKey, endpoint: translationProviderForm.endpoint, region: translationProviderForm.region || '' });
      setTranslationProviderForm((current) => ({ ...current, ...response.data.translationProvider, apiKey: '' }));
      setMessage({ type: 'success', text: 'Automatic translation provider saved securely.' });
    } catch (error) { setMessage({ type: 'error', text: error.response?.data?.message || error.response?.data?.error || 'Could not save translation provider' }); }
    finally { setTranslationProviderBusy(false); }
  };

  /** Purpose: Load translation language rules for the selected bot/environment. Why: Available languages and entries are part of the scoped localization configuration. */
  const fetchBotLanguages = useCallback(async () => {
    if (!selectedBot) return;
    try {
      const response = await axios.get('/api/v1/localizations', { params: { botId: selectedBot, env: selectedEnv } });
      setBotLanguages(response.data.localization || { defaultLanguage: 'en', enabledLanguages: ['en'], entries: [] });
      setAllowedBotLanguages(response.data.allowedLanguages || ['en']);
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Could not load bot languages' });
    }
  }, [selectedBot, selectedEnv]);

  /** Purpose: Persist default language, enabled languages, and translation entries. Why: Runtime localization uses the saved bundle as one coherent unit. */
  const handleSaveBotLanguages = async () => {
    setBotLanguagesBusy(true); setMessage(null);
    try {
      const response = await axios.put('/api/v1/localizations', { botId: selectedBot, env: selectedEnv, defaultLanguage: botLanguages.defaultLanguage, enabledLanguages: botLanguages.enabledLanguages, entries: botLanguages.entries || [] });
      setBotLanguages(response.data.localization);
      setMessage({ type: 'success', text: 'Bot languages saved. Localization now shows these language columns.' });
    } catch (error) { setMessage({ type: 'error', text: error.response?.data?.message || error.response?.data?.error || 'Could not save bot languages' }); }
    finally { setBotLanguagesBusy(false); }
  };

  // Request deduplication - prevent multiple simultaneous requests
  const fetchEnvironmentConfigPromise = useRef(null);
  const lastFetchedKey = useRef(null);

  // Generate widget script
  /** Purpose: Resolve generate widget script in the settings module. Why: This keeps the surrounding interface operation aligned with the expected data shape. */
  const generateWidgetScript = (botId, env) => {
    // Keep widget host aligned with API base URL configuration.
    const backendUrl = API_BASE_URL || window.location.origin;
    const widgetVersion = '20260811-widget-controls-v5';
    const widgetUrl = `${backendUrl}/api/v1/widget/main.js?v=${widgetVersion}`;
    
    return `<script type="text/javascript">
window.vbConfig = Object.assign({}, window.vbConfig || {}, { bot: '${botId}', env: '${env}', apiUrl: '${backendUrl}' });
(function () {
  var w = window, ic = w.VoiceBot;
  if ("function" === typeof ic) {
    ic("reattach_activator");
    ic("update", w.vbConfig);
  } else {
    var d = document, i = function() { i.c(arguments); };
    function l() {
      var e = d.createElement("script");
      e.type = "text/javascript";
      e.async = !0;
      e.src = "${widgetUrl}&t=" + Date.now();
      var t = d.getElementsByTagName("script")[0];
      t.parentNode.insertBefore(e, t);
    }
    i.q = [];
    i.c = function(e) { i.q.push(e); };
    w.VoiceBot = i;
    if (w.attachEvent) {
      w.attachEvent("onload", l);
    } else {
      w.addEventListener("load", l, !1);
    }
  }
})();
</script>`;
  };

  /** Purpose: Load environment configuration while deduplicating concurrent requests for the same scope. Why: Rapid UI changes must not create conflicting stale fetches. */
  const fetchEnvironmentConfig = useCallback(async ({ force = false } = {}) => {
    if (!selectedBot || !selectedEnv) return;
    
    // Create a unique key for this bot+env combination
    const fetchKey = `${selectedBot}-${selectedEnv}`;
    
    // If we're already fetching this exact combination, return the existing promise
    if (!force && fetchEnvironmentConfigPromise.current && lastFetchedKey.current === fetchKey) {
      return fetchEnvironmentConfigPromise.current;
    }
    
    // If we already have the config for this combination, don't fetch again
    if (!force && envConfig && lastFetchedKey.current === fetchKey) {
      return;
    }
    
    lastFetchedKey.current = fetchKey;
    setEnvLoading(true);
    
    // Create new request promise
    fetchEnvironmentConfigPromise.current = (async () => {
      try {
        const response = await axios.get(`/api/v1/environments/${selectedBot}/${selectedEnv}`);
        const env = response.data.environment;
        setEnvConfig(env);
        setEnvForm(createDefaultEnvForm(env.config || {}, selectedBotName));
        resetWhatsAppCredentialDrafts();
        // Clear any previous errors on success
        if (message?.type === 'error' && message?.text?.includes('environment')) {
          setMessage(null);
        }
      } catch (error) {
        // Only log error, don't show error message for 404 (environment might not exist yet)
        if (error.response?.status === 404) {
          // Set default empty form
          setEnvForm(createDefaultEnvForm({}, selectedBotName));
          setEnvConfig(null);
          resetWhatsAppCredentialDrafts();
        } else {
          console.error('Failed to fetch environment config:', error);
          setMessage({ type: 'error', text: 'Failed to load environment configuration' });
        }
      } finally {
        setEnvLoading(false);
        fetchEnvironmentConfigPromise.current = null;
      }
    })();

    return fetchEnvironmentConfigPromise.current;
  }, [selectedBot, selectedEnv, selectedBotName]);

  useEffect(() => {
    setApiKeyInput(apiKey || '');
    setPreviewHomeCommand(localStorage.getItem('preview_home_command') || 'home');
    fetchCurrentApiKey();
    if (isAdmin()) {
      fetchBots();
    }
  }, [apiKey, selectedBot]);

  useEffect(() => {
    if (!selectedBot || !selectedEnv) return;
    axios.get('/api/v1/integrations/contact-center', { params: { botId: selectedBot, env: selectedEnv } }).then((response) => {
      /** Purpose: Find the Genesys integration among configured providers. Why: Contact-center settings need the existing integration's enabled state. */
      const integration = (response.data.integrations || []).find((item) => item.provider === 'genesys');
      if (integration) setGenesysForm((current) => ({ ...current, ...integration.settings, enabled: integration.enabled, clientId: '', clientSecret: '', webhookSecret: '' }));
    }).catch(() => {});
  }, [selectedBot, selectedEnv]);

  /** Purpose: Persist Genesys credentials and settings for this bot/environment. Why: Contact-center connections must remain tenant and environment scoped. */
  const handleSaveGenesys = async () => {
    setGenesysBusy(true);
    try {
      await axios.put('/api/v1/integrations/contact-center/genesys', genesysForm, { params: { botId: selectedBot, env: selectedEnv } });
      setMessage({ type: 'success', text: 'Genesys Cloud integration saved.' });
      setGenesysForm((current) => ({ ...current, clientSecret: '', webhookSecret: '' }));
    } catch (error) { setMessage({ type: 'error', text: error.response?.data?.message || 'Genesys integration could not be saved' }); }
    finally { setGenesysBusy(false); }
  };

  // Fetch flows for the selected bot and environment
  /** Purpose: Load fetch flows for the settings module. Why: The selected view or service needs current scoped data before continuing. */
  const fetchFlows = useCallback(async () => {
    if (!selectedBot || !selectedEnv) return;
    
    setFlowsLoading(true);
    try {
      // Get environment ID first
      const envResponse = await axios.get(`/api/v1/environments/${selectedBot}/${selectedEnv}`);
      const environmentId = envResponse.data.environment?.envId;
      
      if (environmentId) {
        const response = await axios.get(`/api/v1/flows?botId=${selectedBot}&environmentId=${environmentId}`);
        setFlows(response.data.flows || []);
      }
    } catch (error) {
      // If environment doesn't exist yet, flows will be empty
      if (error.response?.status !== 404) {
        console.error('Failed to fetch flows:', error);
      }
      setFlows([]);
    } finally {
      setFlowsLoading(false);
    }
  }, [selectedBot, selectedEnv]);

  /** Purpose: Validate and save environment-wide flow, voice, and channel settings. Why: The server should receive normalized values for the selected environment. */
  const handleSaveEnvironmentConfig = async () => {
    if (!selectedBot || !selectedEnv) {
      setMessage({ type: 'error', text: 'Please select a bot and environment first' });
      return;
    }
    
    const intentConfidenceThreshold = Number(envForm.settings.intentConfidenceThreshold);
    const intentIgnoreThreshold = Number(envForm.settings.intentIgnoreThreshold);
    if (!Number.isFinite(intentConfidenceThreshold) || intentConfidenceThreshold < 0 || intentConfidenceThreshold > 100) {
      setMessage({ type: 'error', text: 'Intent trigger confidence must be between 0% and 100%' });
      return;
    }
    if (!Number.isFinite(intentIgnoreThreshold) || intentIgnoreThreshold < 0 || intentIgnoreThreshold > 100 || intentIgnoreThreshold > intentConfidenceThreshold) {
      setMessage({ type: 'error', text: 'Intent ignore confidence must be between 0% and the trigger confidence.' });
      return;
    }

    setEnvSaving(true);
    setMessage(null);
    const integrationsToSave = getIntegrationsForSave();
    try {
      localStorage.setItem('preview_home_command', (previewHomeCommand || 'home').trim() || 'home');
      // Try to update existing environment
      try {
        await axios.put(`/api/v1/environments/${selectedBot}/${selectedEnv}`, {
          config: {
            twilioPhoneNumber: envForm.twilioPhoneNumber,
            aiPrompt: envForm.aiPrompt,
            voiceSettings: envForm.voiceSettings,
            settings: {
              ...envConfig?.config?.settings,
              intentConfidenceThreshold: Math.round(intentConfidenceThreshold),
              intentIgnoreThreshold: Math.round(intentIgnoreThreshold),
            },
            callFlow: {
              ...envConfig?.config?.callFlow,
              greeting: envForm.callFlow.greeting,
              initialFlowId: envForm.callFlow.initialFlowId,
            },
            chatWidget: envForm.chatWidget,
            integrations: integrationsToSave,
          },
        });
        if (selectedEnv === 'dev' && Array.isArray(botLanguages.enabledLanguages)) {
          try {
            await axios.put('/api/v1/localizations', {
              botId: selectedBot,
              env: 'dev',
              defaultLanguage: botLanguages.defaultLanguage || 'en',
              enabledLanguages: botLanguages.enabledLanguages,
              entries: botLanguages.entries || [],
            });
          } catch (langError) {
            console.warn('Auto-save bot languages failed:', langError);
          }
        }
        setMessage({ type: 'success', text: 'Environment configuration saved successfully!' });
        resetWhatsAppCredentialDrafts();
        fetchEnvironmentConfig({ force: true });
      } catch (updateError) {
        // If 404, environment doesn't exist - create it
        if (updateError.response?.status === 404) {
          await axios.post('/api/v1/environments', {
            botId: selectedBot,
            envName: selectedEnv,
            config: {
              twilioPhoneNumber: envForm.twilioPhoneNumber,
              aiPrompt: envForm.aiPrompt,
              voiceSettings: envForm.voiceSettings,
              settings: {
                intentConfidenceThreshold: Math.round(intentConfidenceThreshold),
                intentIgnoreThreshold: Math.round(intentIgnoreThreshold),
              },
              callFlow: {
                greeting: envForm.callFlow.greeting,
                initialFlowId: envForm.callFlow.initialFlowId,
              },
              chatWidget: envForm.chatWidget,
              integrations: integrationsToSave,
            },
          });
          if (selectedEnv === 'dev' && Array.isArray(botLanguages.enabledLanguages)) {
            try {
              await axios.put('/api/v1/localizations', {
                botId: selectedBot,
                env: 'dev',
                defaultLanguage: botLanguages.defaultLanguage || 'en',
                enabledLanguages: botLanguages.enabledLanguages,
                entries: botLanguages.entries || [],
              });
            } catch (langError) {
              console.warn('Auto-save bot languages failed:', langError);
            }
          }
          setMessage({ type: 'success', text: 'Environment created and configured successfully!' });
          resetWhatsAppCredentialDrafts();
          fetchEnvironmentConfig({ force: true });
        } else {
          throw updateError;
        }
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to save environment configuration',
      });
    } finally {
      setEnvSaving(false);
    }
  };

  /** Purpose: Load apps using the supplied partner token. Why: Onboarding needs the provider's real app list for selection. */
  const loadGupshupApps = async (partnerToken = envForm.integrations.providers?.gupshup?.partnerToken) => {
    if (!selectedBot || !partnerToken) return;
    const response = await axios.post('/api/v1/channels/gupshup/apps', {
      botId: selectedBot, env: selectedEnv, partnerToken,
    });
    setGupshupApps(response.data.partnerAppsList || []);
  };

  /** Purpose: Authenticate the Gupshup partner account and store its returned token in the form. Why: Subsequent app discovery depends on a valid provider session. */
  const handleGupshupLogin = async () => {
    setGupshupBusy(true);
    setMessage(null);
    try {
      const response = await axios.post('/api/v1/channels/gupshup/login', gupshupLoginForm);
      if (!response.data.token) throw new Error('Partner token was not returned');
      updateProviderSection('gupshup', { partnerToken: response.data.token });
      await loadGupshupApps(response.data.token);
      setGupshupLoginForm((current) => ({ ...current, secret: '' }));
      setMessage({ type: 'success', text: 'Gupshup partner account connected. Select an app below.' });
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || error.response?.data?.error || error.message || 'Gupshup login failed' });
    } finally {
      setGupshupBusy(false);
    }
  };

  /** Purpose: Connect the selected Gupshup app after validating required credentials. Why: An incomplete provider setup should not be persisted as connected. */
  const handleGupshupConnect = async () => {
    const config = envForm.integrations.providers?.gupshup || {};
    if (!config.appId || !config.partnerToken) {
      setMessage({ type: 'error', text: 'Login and select a Gupshup app first.' });
      return;
    }
    setGupshupBusy(true);
    try {
      const response = await axios.post('/api/v1/channels/gupshup/connect', {
        botId: selectedBot, env: selectedEnv, appId: config.appId,
        appName: config.appName, partnerToken: config.partnerToken, appToken: config.appToken || config.apiKey,
        setupMode: config.setupMode, accountName: config.accountName, phoneNumber: config.phoneNumber,
        wabaId: config.wabaId, webhookSecret: config.webhookSecret, webhookUrl: config.webhookUrl,
      });
      updateProviderSection('gupshup', response.data.integration || {});
      updateIntegrationSection('whatsapp', { enabled: true, provider: 'gupshup' });
      setMessage({ type: 'success', text: 'Gupshup app connected. V3 webhook subscription is active.' });
      fetchEnvironmentConfig({ force: true });
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || error.response?.data?.error || 'Could not connect Gupshup app' });
    } finally {
      setGupshupBusy(false);
    }
  };

  /** Purpose: Create a Gupshup app after validating its required name and partner token. Why: Provider app creation needs a valid, unique user-selected configuration. */
  const handleGupshupCreateApp = async () => {
    const config = envForm.integrations.providers?.gupshup || {};
    if (!config.partnerToken || !config.appName || config.appName.length < 6) {
      setMessage({ type: 'error', text: 'Login and enter a unique app name of at least 6 characters.' });
      return;
    }
    setGupshupBusy(true);
    try {
      const response = await axios.post('/api/v1/channels/gupshup/apps/create', {
        botId: selectedBot, env: selectedEnv, name: config.appName, partnerToken: config.partnerToken,
      });
      updateProviderSection('gupshup', { appId: response.data.appId || '' });
      await loadGupshupApps(config.partnerToken);
      setMessage({ type: 'success', text: 'Gupshup app created. Click Connect App to activate messaging.' });
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || error.response?.data?.error || 'Could not create Gupshup app' });
    } finally {
      setGupshupBusy(false);
    }
  };

  /** Purpose: Request and open the provider onboarding link. Why: Business verification must be completed through the provider's authorized flow. */
  const handleGupshupOnboard = async () => {
    setGupshupBusy(true);
    try {
      const response = await axios.post('/api/v1/channels/gupshup/onboarding-link', {
        botId: selectedBot, env: selectedEnv,
        user: selectedBotName,
      });
      if (!response.data.link) throw new Error('Onboarding link was not returned');
      updateProviderSection('gupshup', { onboardingLink: response.data.link });
      window.open(response.data.link, '_blank', 'noopener,noreferrer');
      setMessage({ type: 'success', text: 'Gupshup WhatsApp onboarding opened in a new tab.' });
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || error.response?.data?.error || error.message || 'Could not start onboarding' });
    } finally {
      setGupshupBusy(false);
    }
  };

  /** Purpose: Load bots available to the settings operator. Why: Access-control forms need the current workspace bot list. */
  const fetchBots = async () => {
    try {
      const response = await axios.get('/api/v1/admin/bots');
      setBots(response.data.bots || []);
    } catch (error) {
      console.error('Failed to fetch bots:', error);
    }
  };

  /** Purpose: Load bot membership and handle access restrictions. Why: Role editing must use server-authorized membership data. */
  const fetchAccessMembers = useCallback(async () => {
    if (!selectedBot) {
      setAccessMembers([]);
      return;
    }

    setAccessLoading(true);
    try {
      const response = await axios.get(`/api/v1/admin/bots/${selectedBot}/access-control`);
      setAccessMembers(response.data.members || []);
    } catch (error) {
      if (error.response?.status === 403) {
        setAccessMembers([]);
      } else {
        setMessage({
          type: 'error',
          text: error.response?.data?.error || 'Failed to load bot access members',
        });
      }
    } finally {
      setAccessLoading(false);
    }
  }, [selectedBot]);

  // Separate effect for environment config - only when bot/env changes
  useEffect(() => {
    if (selectedBot && selectedEnv) {
      fetchEnvironmentConfig({ force: true });
      fetchFlows();
      fetchAccessMembers();
      fetchAiProvider();
      fetchTranslationProvider();
      fetchBotLanguages();
    }
  }, [selectedBot, selectedEnv, fetchEnvironmentConfig, fetchFlows, fetchAccessMembers, fetchAiProvider, fetchTranslationProvider, fetchBotLanguages]);

  /** Purpose: Validate and invite a workspace member with selected roles and environments. Why: Provisioning requires valid identity and scope information. */
  const handleInviteMember = async () => {
    if (!inviteForm.email || !inviteForm.name) {
      setMessage({ type: 'error', text: 'Email and name are required' });
      return;
    }

    if (inviteForm.password && inviteForm.password.trim().length > 0 && inviteForm.password.trim().length < 6) {
      setMessage({ type: 'error', text: 'Password must be at least 6 characters long' });
      return;
    }

    setInviting(true);
    setMessage(null);
    try {
      const selectedRoles = Array.isArray(inviteForm.workspaceRoles) && inviteForm.workspaceRoles.length > 0
        ? inviteForm.workspaceRoles
        : (inviteForm.workspaceRole ? [inviteForm.workspaceRole] : ['developer']);

      const validRoles = ['admin', 'developer', 'agent_admin', 'agent'];
      /** Purpose: Keep only recognized workspace roles. Why: Unknown role strings should not enter the access update payload. */
      const normalizedRoles = selectedRoles.filter((r) => validRoles.includes(r));
      if (normalizedRoles.length === 0) normalizedRoles.push('developer');

      const primaryRole = normalizedRoles.includes('admin')
        ? 'admin'
        : (normalizedRoles.includes('agent_admin') ? 'agent_admin' : normalizedRoles[0]);

      const hasAdminAccess = normalizedRoles.includes('admin') || normalizedRoles.includes('agent_admin');
      /** Purpose: Identify agent and agent-admin role variants. Why: Agent-specific controls depend on normalized role membership. */
      const isAgent = normalizedRoles.some((r) => ['agent', 'agent_admin'].includes(r));

      const payload = {
        ...inviteForm,
        password: inviteForm.password?.trim() ? inviteForm.password.trim() : undefined,
        workspaceRoles: normalizedRoles,
        workspaceRole: primaryRole,
        role: isAgent ? (normalizedRoles.includes('agent_admin') ? 'agent_admin' : 'agent') : 'developer',
        accessLevel: hasAdminAccess ? 'admin' : 'developer',
        botIds:
          inviteScope === 'access-control' && selectedBot
            ? [selectedBot]
            : inviteForm.botIds,
      };

      const response = inviteScope === 'access-control' && selectedBot
        ? await axios.post(`/api/v1/admin/bots/${selectedBot}/access-control/invite`, payload)
        : await axios.post('/api/v1/admin/users/invite', payload);

      const targetEmail = inviteForm.email;
      const returnedPassword = response.data.tempPassword || (inviteForm.password?.trim() ? inviteForm.password.trim() : null);
      const otp = response.data.otp;

      setMessage({
        type: response.data.emailSent ? 'success' : 'info',
        text: response.data.emailSent
          ? `Invitation details sent directly to ${targetEmail}!`
          : `User access created. Email delivery not configured (check console/SMTP).`,
      });
      setInviteDialogOpen(false);
      setShowInvitePassword(false);
      setInviteScope('team');
      setInviteForm({
        email: '',
        name: '',
        password: '',
        workspaceRoles: ['developer'],
        workspaceRole: 'developer',
        role: 'developer',
        botIds: [],
        environmentsAllowed: ['dev'],
        accessLevel: 'developer',
      });
      if (selectedBot) {
        fetchAccessMembers();
      }
      
      // If email was not delivered and credentials were generated/set, display credentials alert
      if (!response.data.emailSent && returnedPassword) {
        setTimeout(() => {
          alert(`User created!\n\nEmail: ${targetEmail}\nPassword: ${returnedPassword}${otp ? `\nOTP: ${otp}` : ''}\nRoles: ${normalizedRoles.join(', ')}\n\nShare these credentials with the user.`);
        }, 500);
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to invite user',
      });
    } finally {
      setInviting(false);
    }
  };

  /** Purpose: Open the member action menu for the selected user. Why: Role and access actions need a stable target. */
  const handleOpenActionMenu = (event, member) => {
    setActionAnchorEl(event.currentTarget);
    setActiveActionMember(member);
  };

  /** Purpose: Close and clear the active member action menu. Why: A stale target should not remain actionable after dismissal. */
  const handleCloseActionMenu = () => {
    setActionAnchorEl(null);
  };

  /** Purpose: Populate the role editor from the member's current workspace and environment grants. Why: Edits should begin from the actual assigned access. */
  const handleOpenEditRole = (member) => {
    handleCloseActionMenu();
    setEditRoleMember(member);
    const roles = Array.isArray(member?.workspaceRoles) && member.workspaceRoles.length > 0
      ? member.workspaceRoles
      : [member?.workspaceRole || 'developer'];
    setEditRoleValues(roles);
    setEditRoleEnvs(
      Array.isArray(member?.environmentsAllowed) && member.environmentsAllowed.length > 0
        ? member.environmentsAllowed
        : ['dev']
    );
    setEditRoleDialogOpen(true);
  };

  /** Purpose: Persist role and environment grants for the selected bot member. Why: Authorization changes must preserve the explicit scope choices. */
  const handleSaveEditRole = async () => {
    if (!selectedBot || !editRoleMember?.userId) return;
    setEditRoleSaving(true);
    try {
      const response = await axios.put(`/api/v1/admin/bots/${selectedBot}/access-control/${editRoleMember.userId}`, {
        workspaceRoles: editRoleValues,
        environmentsAllowed: editRoleEnvs,
      });
      setMessage({
        type: 'success',
        text: response.data?.message || `Access updated for ${editRoleMember.name || editRoleMember.email}`,
      });
      setEditRoleDialogOpen(false);
      setEditRoleMember(null);
      await fetchAccessMembers();
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to update member roles',
      });
    } finally {
      setEditRoleSaving(false);
    }
  };

  /** Purpose: Open the access removal confirmation for the selected member. Why: Removing bot access should be a deliberate action. */
  const handleOpenDeleteAccess = (member) => {
    handleCloseActionMenu();
    setDeleteAccessMember(member);
    setDeleteAccessDialogOpen(true);
  };

  /** Purpose: Remove the selected member's bot access and refresh state. Why: Revoked access should take effect from server confirmation. */
  const handleConfirmDeleteAccess = async () => {
    if (!selectedBot || !deleteAccessMember?.userId) return;
    setDeleteAccessSaving(true);
    try {
      const response = await axios.delete(`/api/v1/admin/bots/${selectedBot}/access-control/${deleteAccessMember.userId}`);
      setMessage({
        type: 'success',
        text: response.data?.message || `Access removed for ${deleteAccessMember.name || deleteAccessMember.email}`,
      });
      setDeleteAccessDialogOpen(false);
      setDeleteAccessMember(null);
      await fetchAccessMembers();
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to remove member access',
      });
    } finally {
      setDeleteAccessSaving(false);
    }
  };

  /** Purpose: Load the current tenant API key display metadata. Why: Settings must show enough information to identify the key without exposing its secret. */
  const fetchCurrentApiKey = async () => {
    if (!selectedBot && !apiKey) return;
    
    try {
      const response = await axios.get('/api/v1/tenants/me', {
        headers: {
          ...(apiKey ? { 'x-api-key': apiKey } : {}),
          ...(selectedBot ? { 'x-bot-id': selectedBot } : {}),
        },
      });
      if (response.data.tenant?.apiKey) {
        setApiKeyInput(response.data.tenant.apiKey);
        saveApiKey(response.data.tenant.apiKey);
      }
      setApiKeys(response.data.tenant?.apiKeys || []);
    } catch (error) {
      // Silently fail - user might not be authenticated yet
      console.log('Could not fetch API key:', error.message);
    }
  };

  /** Purpose: Create a newly named API key after validating its label. Why: Key rotation must remain explicit and auditable. */
  const handleRegenerateApiKey = async () => {
    const normalizedName = newApiKeyName.trim();
    if (!normalizedName) {
      setMessage({ type: 'error', text: 'Please enter a name for the new API key.' });
      return;
    }

    setRegenerating(true);
    setMessage(null);
    try {
      const response = await axios.post('/api/v1/tenants/regenerate-api-key', {
        name: normalizedName,
      }, {
        headers: {
          ...(apiKey ? { 'x-api-key': apiKey } : {}),
          ...(selectedBot ? { 'x-bot-id': selectedBot } : {}),
        },
      });
      const newApiKey = response.data.apiKey;
      setApiKeyInput(newApiKey);
      saveApiKey(newApiKey);
      setApiKeys(response.data.apiKeys || []);
      setGenerateApiKeyDialogOpen(false);
      setNewApiKeyName('');
      setMessage({ type: 'success', text: `New API key "${normalizedName}" created successfully. Old keys are still active.` });
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.error || 'Error generating API key' 
      });
    } finally {
      setRegenerating(false);
    }
  };

  /** Purpose: Save the API key entered for the local API client. Why: API access requires a nonempty credential before calls can be made. */
  const handleApiKeySave = async () => {
    if (!apiKeyInput.trim()) {
      setMessage({ type: 'error', text: 'API key cannot be empty' });
      return;
    }
    
    setSaving(true);
    setMessage(null);
    try {
      saveApiKey(apiKeyInput.trim());
      setMessage({ type: 'success', text: 'API key saved successfully! Refresh the page to see calls.' });
      // Reload page after 1 second to refresh data
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error) {
      setMessage({ type: 'error', text: 'Error saving API key' });
    } finally {
      setSaving(false);
    }
  };

  /** Purpose: Run the settings page's save feedback handler. Why: The page-level action gives users a clear completion state. */
  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      // Save settings
      setMessage({ type: 'success', text: 'Settings saved successfully' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Error saving settings' });
    } finally {
      setSaving(false);
    }
  };

  const hasBotContext = Boolean(selectedBot);
  const accessControlTabIndex = hasBotContext ? 5 : -1;
  const teamTabIndex = hasBotContext ? 6 : 1;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <StatusNotification message={message} onClose={() => setMessage(null)} />
      
      <Paper
        sx={{
          mb: 3,
          p: 1,
          borderRadius: (theme) => theme.palette.mode === 'dark' ? '12px' : 4,
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 12px 28px rgba(15,23,42,0.06)',
          overflowX: 'auto',
        }}
      >
        <Tabs
          value={settingsTab}
          onChange={(e, v) => setSettingsTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: 52,
            '& .MuiTabs-indicator': {
              height: 0,
            },
            '& .MuiTab-root': {
              minHeight: 44,
              borderRadius: (theme) => theme.palette.mode === 'dark' ? '8px' : 999,
              textTransform: 'none',
              fontWeight: 700,
              color: 'text.secondary',
              px: 2,
            },
            '& .Mui-selected': {
              bgcolor: (theme) => theme.palette.mode === 'dark' ? 'action.selected' : 'primary.main',
              color: (theme) => theme.palette.mode === 'dark' ? '#b8ceff !important' : '#fff !important',
              boxShadow: (theme) => theme.palette.mode === 'dark' ? 'none' : '0 8px 20px rgba(37,99,235,0.28)',
            },
          }}
        >
          <Tab label="API Key" />
          {selectedBot && <Tab label="Environment Config" icon={<SettingsIcon />} iconPosition="start" />}
          {selectedBot && <Tab label="Integrations" icon={<IntegrationInstructionsIcon />} iconPosition="start" />}
          {selectedBot && <Tab label="Chat widget" icon={<WidgetsOutlinedIcon />} iconPosition="start" />}
          {selectedBot && <Tab label="Publish your bot" icon={<PublishIcon />} iconPosition="start" />}
          {selectedBot && <Tab label="Access Control" icon={<SecurityIcon />} iconPosition="start" />}
          {isAdmin() && <Tab label="Team" />}
        </Tabs>
      </Paper>
      
      {settingsTab === 0 && (
      <Paper sx={{ p: 3, mt: 2 }}>
        <Typography variant="h6" gutterBottom>
          API Key Configuration
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Enter your API key to authenticate with the backend. This is required to view calls, leads, and analytics.
        </Typography>
        <TextField
          fullWidth
          type={showApiKey ? 'text' : 'password'}
          label="API Key"
          value={apiKeyInput}
          onChange={(e) => setApiKeyInput(e.target.value)}
          placeholder="Enter your API key"
          sx={{ mt: 2 }}
          helperText={apiKey ? 'API key is set ✓' : 'API key is required to fetch data'}
          InputProps={{
            endAdornment: apiKeyInput && (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowApiKey(!showApiKey)}
                  edge="end"
                >
                  {showApiKey ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            onClick={handleApiKeySave}
            disabled={saving || !apiKeyInput.trim()}
          >
            {saving ? <CircularProgress size={24} /> : 'Save API Key'}
          </Button>
          <Button
            variant="outlined"
            color="warning"
            onClick={() => {
              setNewApiKeyName('');
              setGenerateApiKeyDialogOpen(true);
            }}
            disabled={regenerating}
          >
            {regenerating ? <CircularProgress size={24} /> : 'Generate New API Key'}
          </Button>
        </Box>
        {apiKeys.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
              Saved API Keys
            </Typography>
            <Box sx={{ display: 'grid', gap: 1.25 }}>
              {apiKeys.map((entry) => {
                const isSelected = entry.key === apiKeyInput;
                return (
                  <Paper
                    key={entry.keyId || entry.key}
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      borderRadius: (theme) => theme.palette.mode === 'dark' ? '10px' : 3,
                      borderColor: isSelected ? 'primary.main' : 'divider',
                      bgcolor: isSelected ? 'action.selected' : 'background.paper',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap' }}>
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                            {entry.name}
                          </Typography>
                          {entry.isPrimary && <Chip size="small" label="Newest" color="primary" />}
                          {isSelected && <Chip size="small" label="Selected" color="success" />}
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          {showApiKey ? entry.key : maskApiKey(entry.key)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Button
                          size="small"
                          variant={isSelected ? 'contained' : 'outlined'}
                          onClick={() => {
                            setApiKeyInput(entry.key);
                            saveApiKey(entry.key);
                            setMessage({ type: 'success', text: `API key "${entry.name}" selected.` });
                          }}
                        >
                          Use this key
                        </Button>
                        <Button
                          size="small"
                          variant="text"
                          startIcon={<ContentCopyIcon />}
                          onClick={async () => {
                            await navigator.clipboard.writeText(entry.key);
                            setMessage({ type: 'success', text: `API key "${entry.name}" copied.` });
                          }}
                        >
                          Copy
                        </Button>
                      </Box>
                    </Box>
                  </Paper>
                );
              })}
            </Box>
          </Box>
        )}
        <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
          <strong>Note:</strong> Existing API keys will remain active. Please provide a name when generating a new key, and you can select or copy any saved key from the list anytime.
        </Typography>
      </Paper>
      )}

      <Dialog open={generateApiKeyDialogOpen} onClose={() => !regenerating && setGenerateApiKeyDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Create New API Key</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Provide a descriptive name for the new API key, such as `Production widget`, `CRM sync`, or `Testing key`.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="API Key Name"
            value={newApiKeyName}
            onChange={(e) => setNewApiKeyName(e.target.value)}
            placeholder="Production widget"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !regenerating) {
                handleRegenerateApiKey();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGenerateApiKeyDialogOpen(false)} disabled={regenerating}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleRegenerateApiKey} disabled={regenerating || !newApiKeyName.trim()}>
            {regenerating ? <CircularProgress size={20} /> : 'Create key'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {settingsTab === 1 && selectedBot && (
      <Paper sx={{ p: 3, mt: 2 }}>
        <Typography variant="h6" gutterBottom>
          Environment Configuration
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Configure settings for <strong>{selectedBot}</strong> - <strong>{selectedEnv}</strong> environment
        </Typography>
        
        {!envConfig && !envLoading && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Environment not found. Fill in the form below and save to create it.
          </Alert>
        )}
        
        {envLoading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Paper variant="outlined" sx={(theme) => ({ p: 2.25, mb: 2, borderRadius: 3, borderColor: theme.palette.mode === 'dark' ? 'rgba(52,211,153,0.3)' : 'rgba(16,185,129,0.28)', background: theme.palette.mode === 'dark' ? 'linear-gradient(135deg, rgba(16,185,129,0.1), rgba(15,23,42,0.35))' : 'linear-gradient(135deg, rgba(209,250,229,0.72), rgba(255,255,255,0.96))' })}>
              <Box display="flex" justifyContent="space-between" gap={2} alignItems="flex-start" flexWrap="wrap">
                <Box sx={{ flex: 1, minWidth: 260 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>Bot Languages</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 700 }}>Select the languages this bot supports. These selections control which columns appear for every existing and new key in Localization.</Typography>
                </Box>
                <Chip label={`${botLanguages.enabledLanguages?.length || 1} enabled / ${allowedBotLanguages.length} allowed`} color="success" variant="outlined" />
              </Box>
              {selectedEnv === 'prod' && (
                <Alert
                  severity="info"
                  sx={{ mt: 2, alignItems: 'center', '& .MuiAlert-message': { width: '100%' } }}
                  action={
                    (isAdmin() || isBotAdmin(selectedBot)) && (
                      <Button
                        color="inherit"
                        size="small"
                        variant="outlined"
                        onClick={() => { window.location.href = '/deployments'; }}
                      >
                        Open Release Center
                      </Button>
                    )
                  }
                >
                  PROD languages and environment configuration are read-only. Update them in DEV, then deploy from Release Center with the bot so the release stays in sync.
                </Alert>
              )}
              <Box display="flex" flexWrap="wrap" gap={1} mt={2}>
                {BOT_LANGUAGE_OPTIONS.filter(([code]) => allowedBotLanguages.includes(code) || (botLanguages.enabledLanguages || []).includes(code)).map(([code, label]) => {
                  const selected = (botLanguages.enabledLanguages || ['en']).includes(code);
                  return <FormControlLabel key={code} sx={{ m: 0, px: 1, border: '1px solid', borderColor: selected ? 'success.main' : 'divider', borderRadius: 999, bgcolor: selected ? 'rgba(16,185,129,0.06)' : 'background.paper' }} control={<Checkbox size="small" checked={selected} disabled={!isBotAdmin(selectedBot) || selectedEnv === 'prod' || code === 'en' || (selected && code === botLanguages.defaultLanguage)} onChange={() => setBotLanguages((current) => ({ ...current, enabledLanguages: selected ? current.enabledLanguages.filter((item) => item !== code) : [...current.enabledLanguages, code] }))} />} label={`${label} (${code})`} />;
                })}
              </Box>
              <Box display="flex" gap={2} alignItems="center" flexWrap="wrap" mt={2}>
                <FormControl size="small" sx={{ minWidth: 220 }}><InputLabel>Default language</InputLabel><Select label="Default language" value={botLanguages.defaultLanguage || 'en'} disabled={!isBotAdmin(selectedBot) || selectedEnv === 'prod'} onChange={(event) => setBotLanguages((current) => ({ ...current, defaultLanguage: event.target.value, enabledLanguages: Array.from(new Set([...current.enabledLanguages, event.target.value])) }))}>{BOT_LANGUAGE_OPTIONS.filter(([code]) => (botLanguages.enabledLanguages || []).includes(code)).map(([code, label]) => <MenuItem key={code} value={code}>{label} ({code})</MenuItem>)}</Select></FormControl>
                <Button variant="contained" color="success" disabled={!isBotAdmin(selectedBot) || selectedEnv === 'prod' || botLanguagesBusy} onClick={handleSaveBotLanguages}>{botLanguagesBusy ? <CircularProgress size={20} color="inherit" /> : 'Save Bot Languages'}</Button>
                <Button variant="text" onClick={() => { window.location.href = '/localization'; }}>Open Localization</Button>
              </Box>
            </Paper>

            <Paper
              variant="outlined"
              sx={(theme) => ({
                p: 2.25,
                mb: 2,
                borderRadius: 3,
                borderColor: theme.palette.mode === 'dark' ? 'rgba(34,211,238,0.28)' : 'rgba(37,99,235,0.2)',
                background: theme.palette.mode === 'dark'
                  ? 'linear-gradient(135deg, rgba(8,145,178,0.12), rgba(15,23,42,0.35))'
                  : 'linear-gradient(135deg, rgba(219,234,254,0.7), rgba(255,255,255,0.96))',
              })}
            >
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2} flexWrap="wrap">
                <Box sx={{ flex: 1, minWidth: 240 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                    Intent trigger confidence
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 620 }}>
                    NLU prediction is flow ko tabhi trigger karegi jab confidence is percentage ke barabar ya usse zyada ho. Higher value strict matching deta hai.
                  </Typography>
                </Box>
                <TextField
                  type="number"
                  label="Trigger at"
                  value={envForm.settings.intentConfidenceThreshold}
                  onChange={(event) => setEnvForm((current) => ({
                    ...current,
                    settings: {
                      ...current.settings,
                      intentConfidenceThreshold: event.target.value,
                    },
                  }))}
                  onBlur={() => setEnvForm((current) => ({
                    ...current,
                    settings: {
                      ...current.settings,
                      intentConfidenceThreshold: Math.min(100, Math.max(0, Math.round(Number(current.settings.intentConfidenceThreshold) || 0))),
                    },
                  }))}
                  InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                  inputProps={{ min: 0, max: 100, step: 1 }}
                  sx={{ width: 170 }}
                />
              </Box>
            </Paper>

            <TextField
              fullWidth
              label="Twilio Phone Number"
              value={envForm.twilioPhoneNumber}
              onChange={(e) => setEnvForm({ ...envForm, twilioPhoneNumber: e.target.value })}
              margin="normal"
              placeholder="+1234567890"
              helperText="Enter your Twilio phone number (E.164 format)"
            />
            
            <TextField
              fullWidth
              multiline
              rows={6}
              label="AI System Prompt"
              value={envForm.aiPrompt}
              onChange={(e) => setEnvForm({ ...envForm, aiPrompt: e.target.value })}
              margin="normal"
              placeholder="You are a helpful AI assistant..."
              helperText="Define how the AI should behave during calls"
            />
            
            <Divider sx={{ my: 3 }} />
            
            <Typography variant="subtitle1" gutterBottom>
              Voice Settings
            </Typography>
            
            <FormControl fullWidth margin="normal">
              <InputLabel>Voice Provider</InputLabel>
              <Select
                value={envForm.voiceSettings.provider}
                onChange={(e) => setEnvForm({
                  ...envForm,
                  voiceSettings: { ...envForm.voiceSettings, provider: e.target.value }
                })}
                label="Voice Provider"
              >
                <MenuItem value="elevenlabs">ElevenLabs</MenuItem>
                <MenuItem value="google">Google TTS</MenuItem>
                <MenuItem value="azure">Azure TTS</MenuItem>
              </Select>
            </FormControl>
            
            <TextField
              fullWidth
              label="Voice ID"
              value={envForm.voiceSettings.voiceId}
              onChange={(e) => setEnvForm({
                ...envForm,
                voiceSettings: { ...envForm.voiceSettings, voiceId: e.target.value }
              })}
              margin="normal"
              helperText="Voice ID from your TTS provider"
            />
            
            <TextField
              fullWidth
              label="Language"
              value={envForm.voiceSettings.language}
              onChange={(e) => setEnvForm({
                ...envForm,
                voiceSettings: { ...envForm.voiceSettings, language: e.target.value }
              })}
              margin="normal"
              placeholder="en-US"
            />
            
            <TextField
              fullWidth
              type="number"
              label="Speech Speed"
              value={envForm.voiceSettings.speed}
              onChange={(e) => setEnvForm({
                ...envForm,
                voiceSettings: { ...envForm.voiceSettings, speed: parseFloat(e.target.value) || 1.0 }
              })}
              margin="normal"
              inputProps={{ min: 0.5, max: 2, step: 0.1 }}
              helperText="Speech speed multiplier (0.5 - 2.0)"
            />
            
            <Divider sx={{ my: 3 }} />
            
            <Typography variant="subtitle1" gutterBottom>
              Call Flow Configuration
            </Typography>
            
            <Alert severity="info" sx={{ mb: 2 }}>
              Configure the initial flow that will execute when a call starts. This determines what the bot will say first and where the conversation will go.
            </Alert>
            
            <FormControl fullWidth margin="normal">
              <InputLabel>Initial Flow (First Flow to Execute)</InputLabel>
              <Select
                value={envForm.callFlow.initialFlowId}
                onChange={(e) => setEnvForm({
                  ...envForm,
                  callFlow: { ...envForm.callFlow, initialFlowId: e.target.value }
                })}
                label="Initial Flow (First Flow to Execute)"
                disabled={flowsLoading}
              >
                <MenuItem value="">
                  <em>None - Use default greeting only</em>
                </MenuItem>
                {flows
                  .filter(flow => flow.isActive)
                  .map((flow) => (
                    <MenuItem key={flow.flowId} value={flow.flowId}>
                      {flow.name} {flow.isDefault && '(Default)'}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
            {flowsLoading && (
              <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                Loading flows...
              </Typography>
            )}
            {!flowsLoading && flows.length === 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                No flows found. Create flows in the Flows page first.
              </Typography>
            )}
            
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Greeting Message (Fallback if no flow selected)"
              value={envForm.callFlow.greeting}
              onChange={(e) => setEnvForm({
                ...envForm,
                callFlow: { ...envForm.callFlow, greeting: e.target.value }
              })}
              margin="normal"
              placeholder="Hello! Thank you for calling..."
              helperText="This greeting will be used if no initial flow is selected, or as a fallback"
            />

            <TextField
              fullWidth
              label="Preview Home Command"
              value={previewHomeCommand}
              onChange={(e) => setPreviewHomeCommand(e.target.value)}
              margin="normal"
              placeholder="home"
              helperText="Home button in bot preview will send this command and route to default journey"
            />
            
            <Box display="flex" gap={2} alignItems="center" flexWrap="wrap" sx={{ mt: 3 }}>
              <Button
                variant="contained"
                onClick={handleSaveEnvironmentConfig}
                disabled={envSaving}
              >
                {envSaving ? <CircularProgress size={24} /> : 'Save Environment Configuration'}
              </Button>
            </Box>
          </>
        )}
      </Paper>
      )}
      
      {settingsTab === 2 && selectedBot && (
        <Paper sx={{ p: { xs: 2, md: 3 }, mt: 2, borderRadius: 4 }}>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2} flexWrap="wrap" sx={{ mb: 3 }}>
            <Box>
              <Typography variant="h6" gutterBottom>
                Integrations
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ maxWidth: 720 }}>
                Har provider ka ek clean box hai. Box par click karo, details bharo, aur niche save kar do.
              </Typography>
            </Box>
            <Chip
              label="Environment scoped"
              variant="outlined"
              sx={{ borderRadius: 999, fontWeight: 600 }}
            />
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(3, minmax(0, 1fr))' },
              gap: 2,
              mb: 3,
            }}
          >
            {integrationCards.map((card) => (
              <Paper
                key={card.key}
                variant="outlined"
                onClick={() => setSelectedIntegrationCard(card.key)}
                sx={{
                  ...settingsCardSx,
                  p: 2,
                  cursor: 'pointer',
                  borderColor: selectedIntegrationCard === card.key ? 'primary.main' : 'divider',
                  boxShadow: selectedIntegrationCard === card.key
                    ? '0 0 0 1px rgba(56,189,248,0.35), 0 18px 32px rgba(15,23,42,0.16)'
                    : settingsCardSx.boxShadow,
                }}
              >
                <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={2}>
                  <Box display="flex" alignItems="flex-start" gap={1.5}>
                    {card.icon}
                    <Box>
                      <Typography variant="subtitle1" fontWeight={700}>{card.title}</Typography>
                      <Typography variant="caption" color="text.secondary">{card.subtitle}</Typography>
                    </Box>
                  </Box>
                  <Chip
                    label={card.enabled ? 'Enabled' : 'Setup'}
                    size="small"
                    color={card.enabled ? 'success' : 'default'}
                    variant={card.enabled ? 'filled' : 'outlined'}
                    sx={{ borderRadius: 999 }}
                  />
                </Box>
              </Paper>
            ))}
          </Box>

          <Paper variant="outlined" sx={{ ...settingsCardSx, p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2} flexWrap="wrap" sx={{ mb: 2 }}>
              <Box>
                <Typography variant="subtitle1" fontWeight={700}>
                  {integrationCards.find((card) => card.key === selectedIntegrationCard)?.title || 'Integration details'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {integrationCards.find((card) => card.key === selectedIntegrationCard)?.subtitle || 'Fill details and save.'}
                </Typography>
              </Box>
              <Chip label="Click card to switch" variant="outlined" sx={{ borderRadius: 999 }} />
            </Box>

            {selectedIntegrationCard === 'whatsapp' && (
              <>
                <FormControlLabel control={<Checkbox checked={Boolean(envForm.integrations.whatsapp.enabled)} onChange={(e) => updateIntegrationSection('whatsapp', { enabled: e.target.checked })} />} label="Enable WhatsApp" />
                <FormControl fullWidth margin="normal">
                  <InputLabel>Provider</InputLabel>
                  <Select value={envForm.integrations.whatsapp.provider} label="Provider" onChange={(e) => updateIntegrationSection('whatsapp', { provider: e.target.value })}>
                    <MenuItem value="meta">Meta Cloud API</MenuItem>
                    <MenuItem value="gupshup">Gupshup</MenuItem>
                    <MenuItem value="twilio">Twilio</MenuItem>
                    <MenuItem value="interakt">Interakt</MenuItem>
                    <MenuItem value="msg91">MSG91</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  fullWidth
                  label="Meta Access Token"
                  type="password"
                  value={whatsappCredentialEditing.accessToken || !envForm.integrations.whatsapp.accessTokenConfigured
                    ? whatsappCredentialDrafts.accessToken
                    : 'Stored securely'}
                  onChange={(event) => setWhatsappCredentialDrafts((current) => ({ ...current, accessToken: event.target.value }))}
                  margin="normal"
                  helperText={envForm.integrations.whatsapp.accessTokenConfigured
                    ? whatsappCredentialEditing.accessToken ? 'Enter a replacement token. Leave blank to keep the current token.' : 'Configured token is masked. Select Replace to update it.'
                    : 'Enter your Meta Cloud API access token.'}
                  InputProps={{
                    readOnly: Boolean(envForm.integrations.whatsapp.accessTokenConfigured) && !whatsappCredentialEditing.accessToken,
                    endAdornment: Boolean(envForm.integrations.whatsapp.accessTokenConfigured) && !whatsappCredentialEditing.accessToken ? (
                      <InputAdornment position="end">
                        <Button size="small" onClick={() => setWhatsappCredentialEditing((current) => ({ ...current, accessToken: true }))}>Replace</Button>
                      </InputAdornment>
                    ) : undefined,
                  }}
                />
                <TextField fullWidth label="Phone Number ID" value={envForm.integrations.whatsapp.phoneNumberId} onChange={(e) => updateIntegrationSection('whatsapp', { phoneNumberId: e.target.value })} margin="normal" />
                <TextField fullWidth label="Meta App ID" value={envForm.integrations.whatsapp.appId || ''} onChange={(e) => updateIntegrationSection('whatsapp', { appId: e.target.value })} margin="normal" helperText="Used for WhatsApp template media uploads. Can also be supplied by the server's WHATSAPP_APP_ID or META_APP_ID environment variable." />
                <TextField fullWidth label="WhatsApp Business Account ID (WABA ID)" value={envForm.integrations.whatsapp.wabaId || ''} onChange={(e) => updateIntegrationSection('whatsapp', { wabaId: e.target.value })} margin="normal" helperText="Required to sync and submit message templates. Find this in Meta Business Settings." />
                {envForm.integrations.whatsapp.provider === 'meta' && <Box sx={{ mt: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} justifyContent="space-between" spacing={1}>
                    <Box><Typography fontWeight={700}>Additional WhatsApp senders</Typography><Typography variant="body2" color="text.secondary">Use stable sender IDs in the outbound API. The access token and WABA above are shared; each sender must belong to this WABA.</Typography></Box>
                    <Button variant="outlined" onClick={addWhatsAppSender}>Add sender</Button>
                  </Stack>
                  {(envForm.integrations.whatsapp.senders || []).map((sender, index) => <Box key={`${sender.senderId || 'sender'}-${index}`} sx={{ mt: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }}>
                      <TextField fullWidth label="Sender ID (API selector)" value={sender.senderId} onChange={(event) => updateWhatsAppSender(index, { senderId: event.target.value })} helperText="Unique stable ID, e.g. sales_in" />
                      <TextField fullWidth label="Meta Phone Number ID" value={sender.phoneNumberId} onChange={(event) => updateWhatsAppSender(index, { phoneNumberId: event.target.value })} />
                      <TextField fullWidth label="Display phone number (optional)" value={sender.displayPhoneNumber} onChange={(event) => updateWhatsAppSender(index, { displayPhoneNumber: event.target.value })} />
                      <FormControlLabel control={<Checkbox checked={sender.enabled !== false} onChange={(event) => updateWhatsAppSender(index, { enabled: event.target.checked })} />} label="Enabled" />
                      <IconButton aria-label={`Remove sender ${sender.senderId || index + 1}`} color="error" onClick={() => removeWhatsAppSender(index)}><DeleteOutlineIcon /></IconButton>
                    </Stack>
                  </Box>)}
                </Box>}
                <TextField
                  fullWidth
                  label="Verify Token"
                  type="password"
                  value={whatsappCredentialEditing.verifyToken || !envForm.integrations.whatsapp.verifyTokenConfigured
                    ? whatsappCredentialDrafts.verifyToken
                    : 'Stored securely'}
                  onChange={(event) => setWhatsappCredentialDrafts((current) => ({ ...current, verifyToken: event.target.value }))}
                  margin="normal"
                  helperText={envForm.integrations.whatsapp.verifyTokenConfigured
                    ? whatsappCredentialEditing.verifyToken ? 'Enter a replacement token. Leave blank to keep the current token.' : 'Configured token is masked. Select Replace to update it.'
                    : 'Enter the environment token used by your WhatsApp integration.'}
                  InputProps={{
                    readOnly: Boolean(envForm.integrations.whatsapp.verifyTokenConfigured) && !whatsappCredentialEditing.verifyToken,
                    endAdornment: Boolean(envForm.integrations.whatsapp.verifyTokenConfigured) && !whatsappCredentialEditing.verifyToken ? (
                      <InputAdornment position="end">
                        <Button size="small" onClick={() => setWhatsappCredentialEditing((current) => ({ ...current, verifyToken: true }))}>Replace</Button>
                      </InputAdornment>
                    ) : undefined,
                  }}
                />
                <TextField fullWidth label="Graph Version" value={envForm.integrations.whatsapp.graphVersion} onChange={(e) => updateIntegrationSection('whatsapp', { graphVersion: e.target.value })} margin="normal" />
                <TextField
                  fullWidth
                  type="number"
                  label="Conversation inactivity timeout (minutes)"
                  value={envForm.integrations.whatsapp.sessionTimeoutMinutes}
                  onChange={(e) => updateIntegrationSection('whatsapp', { sessionTimeoutMinutes: Math.min(1440, Math.max(1, Number(e.target.value) || 30)) })}
                  inputProps={{ min: 1, max: 1440 }}
                  helperText="Messages reuse the active WhatsApp session. After this idle period, the next message starts a new session."
                  margin="normal"
                />
                <TextField fullWidth label="Webhook URL" value={envForm.integrations.whatsapp.webhookUrl} onChange={(e) => updateIntegrationSection('whatsapp', { webhookUrl: e.target.value })} margin="normal" />
              </>
            )}

            {selectedIntegrationCard === 'ai_provider' && (
              <>
                <FormControlLabel control={<Checkbox checked={Boolean(aiProviderForm.enabled)} onChange={(event) => setAiProviderForm((current) => ({ ...current, enabled: event.target.checked }))} />} label="Enable Dynamic Chat AI for this tenant" />
                <Alert severity="info" sx={{ my: 1 }}>
                  One encrypted tenant credential is shared by that tenant&apos;s bots. Dynamic Chat bypasses intent NLU and has separate success/failure paths.
                </Alert>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Provider</InputLabel>
                  <Select value={aiProviderForm.provider} label="Provider" onChange={(event) => setAiProviderForm((current) => ({ ...current, provider: event.target.value }))}>
                    <MenuItem value="openai">OpenAI</MenuItem>
                    <MenuItem value="openai_compatible">OpenAI-compatible</MenuItem>
                  </Select>
                </FormControl>
                <TextField fullWidth type="password" label={aiProviderForm.credentialsConfigured ? 'Replace API key (optional)' : 'API key'} value={aiProviderForm.apiKey} placeholder={aiProviderForm.credentialsConfigured ? (aiProviderForm.apiKeyHint || '********') : ''} InputLabelProps={aiProviderForm.credentialsConfigured ? { shrink: true } : undefined} onChange={(event) => setAiProviderForm((current) => ({ ...current, apiKey: event.target.value }))} margin="normal" helperText={aiProviderForm.credentialsConfigured ? 'Leave blank to keep the current key.' : 'Stored encrypted; never returned to the browser.'} />
                <TextField fullWidth label="Model" value={aiProviderForm.model} onChange={(event) => setAiProviderForm((current) => ({ ...current, model: event.target.value }))} margin="normal" />
                <TextField fullWidth label="Base URL" value={aiProviderForm.baseUrl} onChange={(event) => setAiProviderForm((current) => ({ ...current, baseUrl: event.target.value }))} margin="normal" />
                <TextField fullWidth label="Organization (optional)" value={aiProviderForm.organization} onChange={(event) => setAiProviderForm((current) => ({ ...current, organization: event.target.value }))} margin="normal" />
                <Button variant="contained" disabled={aiProviderBusy || !isAdmin} onClick={handleSaveAiProvider} sx={{ mt: 1 }}>
                  {aiProviderBusy ? <CircularProgress size={20} /> : 'Save AI Provider'}
                </Button>
              </>
            )}

            {selectedIntegrationCard === 'translation_provider' && (
              <>
                <FormControlLabel control={<Checkbox checked={Boolean(translationProviderForm.enabled)} onChange={(event) => setTranslationProviderForm((current) => ({ ...current, enabled: event.target.checked }))} />} label="Enable automatic translation for this tenant" />
                <Alert severity="info" sx={{ my: 1 }}>API key encrypted store hoti hai. Auto Translate only creates a draft; save localization in DEV, then ship it with the bot through Release Center.</Alert>
                <FormControl fullWidth margin="normal"><InputLabel>Provider</InputLabel><Select value={translationProviderForm.provider} label="Provider" onChange={(event) => { const provider = event.target.value; const endpoints = { google: 'https://translation.googleapis.com/language/translate/v2', azure: 'https://api.cognitive.microsofttranslator.com', deepl: 'https://api-free.deepl.com/v2/translate' }; setTranslationProviderForm((current) => ({ ...current, provider, endpoint: endpoints[provider] })); }}><MenuItem value="google">Google Cloud Translation</MenuItem><MenuItem value="azure">Azure Translator</MenuItem><MenuItem value="deepl">DeepL</MenuItem></Select></FormControl>
                <TextField fullWidth type="password" label={translationProviderForm.credentialsConfigured ? 'Replace API key (optional)' : 'API key'} value={translationProviderForm.apiKey} placeholder={translationProviderForm.credentialsConfigured ? (translationProviderForm.apiKeyHint || '********') : ''} InputLabelProps={translationProviderForm.credentialsConfigured ? { shrink: true } : undefined} onChange={(event) => setTranslationProviderForm((current) => ({ ...current, apiKey: event.target.value }))} margin="normal" helperText={translationProviderForm.credentialsConfigured ? 'Leave blank to keep the current key.' : 'Required when enabling automatic translation.'} />
                <TextField fullWidth label="Provider endpoint" value={translationProviderForm.endpoint} onChange={(event) => setTranslationProviderForm((current) => ({ ...current, endpoint: event.target.value }))} margin="normal" />
                {translationProviderForm.provider === 'azure' && <TextField fullWidth label="Azure region" value={translationProviderForm.region} onChange={(event) => setTranslationProviderForm((current) => ({ ...current, region: event.target.value }))} margin="normal" placeholder="centralindia" />}
                <Button variant="contained" disabled={translationProviderBusy || !isAdmin()} onClick={handleSaveTranslationProvider} sx={{ mt: 1 }}>{translationProviderBusy ? <CircularProgress size={20} /> : 'Save Translation Provider'}</Button>
              </>
            )}

            {selectedIntegrationCard === 'genesys' && (
              <>
                <FormControlLabel control={<Checkbox checked={genesysForm.enabled} onChange={(event) => setGenesysForm((current) => ({ ...current, enabled: event.target.checked }))} />} label="Enable Genesys Cloud" />
                <Alert severity="info" sx={{ my: 1 }}>Credentials encrypted store hote hain. Callback: /api/v1/provider-events/genesys/{selectedBot} with x-provider-secret header.</Alert>
                {[['clientId','OAuth Client ID'],['clientSecret','OAuth Client Secret'],['webhookSecret','Webhook Secret'],['loginBaseUrl','Login base URL'],['apiBaseUrl','API base URL'],['createConversationPath','Create conversation path'],['sendMessagePath','Send message path (:conversationId)'],['sendAttachmentPath','Send attachment path (:conversationId)'],['transferPath','Transfer path (:conversationId)'],['statusPath','Status path (:conversationId)']].map(([key,label]) => <TextField key={key} fullWidth type={key.toLowerCase().includes('secret') ? 'password' : 'text'} label={label} value={genesysForm[key] || ''} onChange={(event) => setGenesysForm((current) => ({ ...current, [key]: event.target.value }))} margin="normal" />)}
                <Button variant="contained" disabled={genesysBusy} onClick={handleSaveGenesys}>{genesysBusy ? <CircularProgress size={20} /> : 'Save Genesys Integration'}</Button>
              </>
            )}

            {selectedIntegrationCard === 'voice' && (
              <>
                <FormControlLabel control={<Checkbox checked={Boolean(envForm.integrations.voice.enabled)} onChange={(e) => updateIntegrationSection('voice', { enabled: e.target.checked })} />} label="Enable Voice Bot" />
                <FormControl fullWidth margin="normal">
                  <InputLabel>Voice Provider</InputLabel>
                  <Select value={envForm.integrations.voice.provider} label="Voice Provider" onChange={(e) => updateIntegrationSection('voice', { provider: e.target.value })}>
                    <MenuItem value="genesis">Genesis</MenuItem>
                    <MenuItem value="twilio">Twilio</MenuItem>
                    <MenuItem value="exotel">Exotel</MenuItem>
                    <MenuItem value="custom">Custom</MenuItem>
                  </Select>
                </FormControl>
                <TextField fullWidth label="Provider API Key" value={envForm.integrations.voice.apiKey} onChange={(e) => updateIntegrationSection('voice', { apiKey: e.target.value })} margin="normal" />
                <TextField fullWidth label="Project / Account ID" value={envForm.integrations.voice.projectId} onChange={(e) => updateIntegrationSection('voice', { projectId: e.target.value })} margin="normal" />
                <TextField fullWidth label="Voice Webhook URL" value={envForm.integrations.voice.webhookUrl} onChange={(e) => updateIntegrationSection('voice', { webhookUrl: e.target.value })} margin="normal" />
              </>
            )}

            {selectedIntegrationCard === 'payments' && (
              <>
                <FormControlLabel control={<Checkbox checked={Boolean(envForm.integrations.payments.enabled)} onChange={(e) => updateIntegrationSection('payments', { enabled: e.target.checked })} />} label="Enable Payments" />
                <FormControl fullWidth margin="normal">
                  <InputLabel>Payment Provider</InputLabel>
                  <Select value={envForm.integrations.payments.provider} label="Payment Provider" onChange={(e) => updateIntegrationSection('payments', { provider: e.target.value })}>
                    <MenuItem value="razorpay">Razorpay</MenuItem>
                    <MenuItem value="cashfree">Cashfree</MenuItem>
                    <MenuItem value="stripe">Stripe</MenuItem>
                  </Select>
                </FormControl>
                <TextField fullWidth label="Key ID" value={envForm.integrations.payments.keyId} onChange={(e) => updateIntegrationSection('payments', { keyId: e.target.value })} margin="normal" />
                <TextField fullWidth label="Key Secret" value={envForm.integrations.payments.keySecret} onChange={(e) => updateIntegrationSection('payments', { keySecret: e.target.value })} margin="normal" />
                <TextField fullWidth label="Webhook Secret" value={envForm.integrations.payments.webhookSecret} onChange={(e) => updateIntegrationSection('payments', { webhookSecret: e.target.value })} margin="normal" />
                <TextField fullWidth label="Currency" value={envForm.integrations.payments.currency} onChange={(e) => updateIntegrationSection('payments', { currency: e.target.value.toUpperCase() })} margin="normal" />
              </>
            )}

            {selectedIntegrationCard === 'common' && (
              <>
                <TextField fullWidth label="CRM Webhook URL" value={envForm.integrations.common.crmWebhookUrl} onChange={(e) => updateIntegrationSection('common', { crmWebhookUrl: e.target.value })} margin="normal" />
                <TextField fullWidth label="Analytics Webhook URL" value={envForm.integrations.common.analyticsWebhookUrl} onChange={(e) => updateIntegrationSection('common', { analyticsWebhookUrl: e.target.value })} margin="normal" />
                <TextField fullWidth label="Automation Webhook URL" value={envForm.integrations.common.automationWebhookUrl} onChange={(e) => updateIntegrationSection('common', { automationWebhookUrl: e.target.value })} margin="normal" />
                <TextField fullWidth label="Support Email" value={envForm.integrations.common.supportEmail} onChange={(e) => updateIntegrationSection('common', { supportEmail: e.target.value })} margin="normal" />
                <TextField fullWidth multiline rows={3} label="Notes" value={envForm.integrations.common.notes} onChange={(e) => updateIntegrationSection('common', { notes: e.target.value })} margin="normal" />
              </>
            )}

            {selectedIntegrationCard === 'twilio' && (
              <>
                <FormControlLabel control={<Checkbox checked={Boolean(envForm.integrations.providers?.twilio?.enabled)} onChange={(e) => updateProviderSection('twilio', { enabled: e.target.checked })} />} label="Enable Twilio" />
                <TextField fullWidth label="Account SID" value={envForm.integrations.providers?.twilio?.accountSid || ''} onChange={(e) => updateProviderSection('twilio', { accountSid: e.target.value })} margin="normal" />
                <TextField fullWidth label="Auth Token" value={envForm.integrations.providers?.twilio?.authToken || ''} onChange={(e) => updateProviderSection('twilio', { authToken: e.target.value })} margin="normal" />
                <TextField fullWidth label="Phone Number" value={envForm.integrations.providers?.twilio?.phoneNumber || ''} onChange={(e) => updateProviderSection('twilio', { phoneNumber: e.target.value })} margin="normal" />
                <TextField fullWidth label="Webhook URL" value={envForm.integrations.providers?.twilio?.webhookUrl || ''} onChange={(e) => updateProviderSection('twilio', { webhookUrl: e.target.value })} margin="normal" />
              </>
            )}

            {selectedIntegrationCard === 'gupshup' && (
              <>
                <FormControlLabel control={<Checkbox checked={Boolean(envForm.integrations.providers?.gupshup?.enabled)} onChange={(e) => updateProviderSection('gupshup', { enabled: e.target.checked })} />} label="Enable Gupshup" />
                <Alert severity="info" sx={{ my: 1 }}>
                  Partner Login optional hai. Existing App ID aur App Access Token ho to Manual Credentials select karke direct connect kar sakte hain.
                </Alert>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Setup Method</InputLabel>
                  <Select value={envForm.integrations.providers?.gupshup?.setupMode || 'partner'} label="Setup Method" onChange={(e) => updateProviderSection('gupshup', { setupMode: e.target.value })}>
                    <MenuItem value="partner">Partner Login (automatic)</MenuItem>
                    <MenuItem value="manual">Manual Credentials</MenuItem>
                  </Select>
                </FormControl>
                {(envForm.integrations.providers?.gupshup?.setupMode || 'partner') === 'partner' && (
                  <>
                    <TextField fullWidth label="Partner Email (optional)" value={gupshupLoginForm.email} onChange={(e) => setGupshupLoginForm((current) => ({ ...current, email: e.target.value }))} margin="normal" />
                    <TextField fullWidth type="password" label="Partner Client Secret (optional)" value={gupshupLoginForm.secret} onChange={(e) => setGupshupLoginForm((current) => ({ ...current, secret: e.target.value }))} margin="normal" helperText="Sirf login ke liye use hota hai; save nahi hota." />
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', my: 1 }}>
                      <Button variant="outlined" disabled={gupshupBusy || !gupshupLoginForm.email || !gupshupLoginForm.secret} onClick={handleGupshupLogin}>
                        {gupshupBusy ? <CircularProgress size={18} /> : 'Login & Load Apps'}
                      </Button>
                      {envForm.integrations.providers?.gupshup?.partnerToken && <Button variant="text" disabled={gupshupBusy} onClick={() => loadGupshupApps().catch((error) => setMessage({ type: 'error', text: error.response?.data?.message || 'Could not load Gupshup apps' }))}>Refresh Apps</Button>}
                    </Box>
                    <FormControl fullWidth margin="normal">
                      <InputLabel>Gupshup App</InputLabel>
                      <Select value={envForm.integrations.providers?.gupshup?.appId || ''} label="Gupshup App" onChange={(e) => {
                        /** Purpose: Resolve the selected app from the form value. Why: Provider connection updates need the currently chosen app. */
                        const app = gupshupApps.find((item) => item.id === e.target.value);
                        updateProviderSection('gupshup', { appId: e.target.value, appName: app?.name || '', phoneNumber: app?.phone || '' });
                      }}>
                        {gupshupApps.map((app) => <MenuItem key={app.id} value={app.id}>{app.name} {app.phone ? `(${app.phone})` : ''}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </>
                )}
                <TextField fullWidth label="Account Name" value={envForm.integrations.providers?.gupshup?.accountName || ''} onChange={(e) => updateProviderSection('gupshup', { accountName: e.target.value })} margin="normal" placeholder="e.g., Main Business Line" />
                <TextField fullWidth label="App ID" value={envForm.integrations.providers?.gupshup?.appId || ''} onChange={(e) => updateProviderSection('gupshup', { appId: e.target.value })} margin="normal" />
                {(envForm.integrations.providers?.gupshup?.setupMode || 'partner') === 'partner' && <TextField fullWidth label="New App Name" value={envForm.integrations.providers?.gupshup?.appName || ''} onChange={(e) => updateProviderSection('gupshup', { appName: e.target.value })} margin="normal" helperText="Sirf naya Gupshup app create karne ke liye." />}
                <TextField fullWidth label="Gupshup Partner App Access Token" type="password" value={envForm.integrations.providers?.gupshup?.appToken || ''} onChange={(e) => updateProviderSection('gupshup', { appToken: e.target.value, apiKey: e.target.value })} margin="normal" placeholder="sk_..." helperText="Manual mode mein required; Partner Login mode mein automatically generate hota hai." />
                <TextField fullWidth label="Registered WhatsApp Phone Number" value={envForm.integrations.providers?.gupshup?.phoneNumber || ''} onChange={(e) => updateProviderSection('gupshup', { phoneNumber: e.target.value.replace(/[^0-9]/g, '') })} margin="normal" placeholder="919876543210" />
                <TextField fullWidth label="WABA ID (optional)" value={envForm.integrations.providers?.gupshup?.wabaId || ''} onChange={(e) => updateProviderSection('gupshup', { wabaId: e.target.value })} margin="normal" />
                <TextField fullWidth label="Webhook Signing Secret (optional)" value={envForm.integrations.providers?.gupshup?.webhookSecret || ''} onChange={(e) => updateProviderSection('gupshup', { webhookSecret: e.target.value })} margin="normal" />
                <TextField fullWidth label="API Version" value={envForm.integrations.providers?.gupshup?.apiVersion || 'v3'} InputProps={{ readOnly: true }} margin="normal" />
                <TextField
                  fullWidth
                  label="Webhook URL"
                  value={envForm.integrations.providers?.gupshup?.webhookUrl || ''}
                  onChange={(e) => updateProviderSection('gupshup', { webhookUrl: e.target.value.trim() })}
                  margin="normal"
                  placeholder="https://your-domain.com/webhook/whatsapp"
                  helperText="Isi VoiceBot backend ka public URL hona chahiye aur path /webhook/whatsapp par end hona chahiye. Kisi doosre app ka /api/webhook URL kaam nahi karega."
                />
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                  {(envForm.integrations.providers?.gupshup?.setupMode || 'partner') === 'partner' && <Button variant="outlined" disabled={gupshupBusy || !envForm.integrations.providers?.gupshup?.partnerToken} onClick={handleGupshupCreateApp}>Create App</Button>}
                  <Button variant="contained" disabled={gupshupBusy || !envForm.integrations.providers?.gupshup?.appId || (!envForm.integrations.providers?.gupshup?.appToken && !envForm.integrations.providers?.gupshup?.partnerToken)} onClick={handleGupshupConnect}>Connect & Subscribe</Button>
                  {(envForm.integrations.providers?.gupshup?.setupMode || 'partner') === 'partner' && <Button variant="outlined" disabled={gupshupBusy || !envForm.integrations.providers?.gupshup?.appToken} onClick={handleGupshupOnboard}>Onboard WhatsApp Number</Button>}
                </Box>
              </>
            )}

            {selectedIntegrationCard === 'msg91' && (
              <>
                <FormControlLabel control={<Checkbox checked={Boolean(envForm.integrations.providers?.msg91?.enabled)} onChange={(e) => updateProviderSection('msg91', { enabled: e.target.checked })} />} label="Enable MSG91" />
                <TextField fullWidth label="Auth Key" value={envForm.integrations.providers?.msg91?.authKey || ''} onChange={(e) => updateProviderSection('msg91', { authKey: e.target.value })} margin="normal" />
                <TextField fullWidth label="Flow ID" value={envForm.integrations.providers?.msg91?.flowId || ''} onChange={(e) => updateProviderSection('msg91', { flowId: e.target.value })} margin="normal" />
                <TextField fullWidth label="Sender ID" value={envForm.integrations.providers?.msg91?.senderId || ''} onChange={(e) => updateProviderSection('msg91', { senderId: e.target.value })} margin="normal" />
                <TextField fullWidth label="Webhook URL" value={envForm.integrations.providers?.msg91?.webhookUrl || ''} onChange={(e) => updateProviderSection('msg91', { webhookUrl: e.target.value })} margin="normal" />
              </>
            )}

            {selectedIntegrationCard === 'interakt' && (
              <>
                <FormControlLabel control={<Checkbox checked={Boolean(envForm.integrations.providers?.interakt?.enabled)} onChange={(e) => updateProviderSection('interakt', { enabled: e.target.checked })} />} label="Enable Interakt" />
                <TextField fullWidth label="API Key" value={envForm.integrations.providers?.interakt?.apiKey || ''} onChange={(e) => updateProviderSection('interakt', { apiKey: e.target.value })} margin="normal" />
                <TextField fullWidth label="Workspace ID" value={envForm.integrations.providers?.interakt?.workspaceId || ''} onChange={(e) => updateProviderSection('interakt', { workspaceId: e.target.value })} margin="normal" />
                <TextField fullWidth label="Phone Number" value={envForm.integrations.providers?.interakt?.phoneNumber || ''} onChange={(e) => updateProviderSection('interakt', { phoneNumber: e.target.value })} margin="normal" />
                <TextField fullWidth label="Webhook URL" value={envForm.integrations.providers?.interakt?.webhookUrl || ''} onChange={(e) => updateProviderSection('interakt', { webhookUrl: e.target.value })} margin="normal" />
              </>
            )}

            {selectedIntegrationCard === 'exotel' && (
              <>
                <FormControlLabel control={<Checkbox checked={Boolean(envForm.integrations.providers?.exotel?.enabled)} onChange={(e) => updateProviderSection('exotel', { enabled: e.target.checked })} />} label="Enable Exotel" />
                <TextField fullWidth label="SID" value={envForm.integrations.providers?.exotel?.sid || ''} onChange={(e) => updateProviderSection('exotel', { sid: e.target.value })} margin="normal" />
                <TextField fullWidth label="API Key" value={envForm.integrations.providers?.exotel?.apiKey || ''} onChange={(e) => updateProviderSection('exotel', { apiKey: e.target.value })} margin="normal" />
                <TextField fullWidth label="API Token" value={envForm.integrations.providers?.exotel?.apiToken || ''} onChange={(e) => updateProviderSection('exotel', { apiToken: e.target.value })} margin="normal" />
                <TextField fullWidth label="Subdomain" value={envForm.integrations.providers?.exotel?.subdomain || ''} onChange={(e) => updateProviderSection('exotel', { subdomain: e.target.value })} margin="normal" />
              </>
            )}

            {!['ai_provider', 'translation_provider', 'genesys'].includes(selectedIntegrationCard) && <Box sx={{ mt: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button variant="contained" onClick={handleSaveEnvironmentConfig} disabled={envSaving}>
                {envSaving ? <CircularProgress size={20} /> : 'Save Integrations'}
              </Button>
              <Alert severity="info" sx={{ flex: 1, minWidth: 240 }}>
                Select a provider, enter the required details, and save your changes. The configuration will be stored for the selected environment.
              </Alert>
            </Box>}
          </Paper>
        </Paper>
      )}

      {settingsTab === 3 && selectedBot && (
        <Paper sx={{ p: { xs: 1.75, md: 2.5 }, mt: 2, borderRadius: 4, overflow: 'visible' }}>
          <Box
            display="grid"
            gridTemplateColumns={{ xs: '1fr', lg: 'minmax(0,1.05fr) minmax(360px,0.95fr)' }}
            gap={{ xs: 2, lg: 2.5 }}
            alignItems="start"
          >
            <Box sx={{ display: 'grid', gap: 1.5 }}>
              <Typography variant="h6" gutterBottom sx={{ mb: 0.5 }}>
                Chat widget
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                Website chat widget ka avatar, colors, size, position aur welcome content yahin configure karo.
              </Typography>

              <Paper variant="outlined" sx={{ ...chatWidgetCardSx }}>
                <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ fontSize: '1rem', mb: 1 }}>
                  Bot identity
                </Typography>
                <Box display="grid" gap={1.5}>
                  <TextField
                    fullWidth
                    label="Widget title"
                    value={envForm.chatWidget.title}
                    onChange={(e) => updateChatWidget({ title: e.target.value })}
                  />
                  <TextField
                    fullWidth
                    label="Widget subtitle"
                    value={envForm.chatWidget.subtitle}
                    onChange={(e) => updateChatWidget({ subtitle: e.target.value })}
                  />
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      border: '1px solid',
                      borderColor: 'divider',
                      bgcolor: 'background.paper',
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      gap: 2.5,
                    }}
                  >
                    <Box
                      sx={{
                        width: 60,
                        height: 60,
                        borderRadius: '50%',
                        overflow: 'hidden',
                        border: '2px solid',
                        borderColor: 'primary.main',
                        bgcolor: 'action.hover',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        boxShadow: 1,
                      }}
                    >
                      {envForm.chatWidget.avatarUrl ? (
                        <Box
                          component="img"
                          src={envForm.chatWidget.avatarUrl}
                          alt="Avatar preview"
                          sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                            if (e.currentTarget.nextSibling) {
                              e.currentTarget.nextSibling.style.display = 'inline';
                            }
                          }}
                        />
                      ) : null}
                      <Typography
                        variant="h6"
                        fontWeight={700}
                        color="primary.main"
                        sx={{ display: envForm.chatWidget.avatarUrl ? 'none' : 'inline' }}
                      >
                        {envForm.chatWidget.avatarText || resolvedChatWidgetPreview.avatarText || 'VB'}
                      </Typography>
                    </Box>

                    <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
                      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                        Bot Avatar
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                        Upload an avatar image (PNG, JPG, SVG, WebP up to 10MB) or enter an image URL.
                      </Typography>

                      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" sx={{ mb: 1.5 }}>
                        <input
                          type="file"
                          ref={avatarFileInputRef}
                          accept="image/*"
                          style={{ display: 'none' }}
                          onChange={handleAvatarUpload}
                        />
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={avatarUploading ? <CircularProgress size={16} color="inherit" /> : <PhotoCameraIcon />}
                          onClick={() => avatarFileInputRef.current?.click()}
                          disabled={avatarUploading}
                          sx={{ borderRadius: 2 }}
                        >
                          {avatarUploading ? 'Uploading...' : envForm.chatWidget.avatarUrl ? 'Change image' : 'Upload image'}
                        </Button>

                        {envForm.chatWidget.avatarUrl && (
                          <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            startIcon={<DeleteOutlineIcon />}
                            onClick={() => updateChatWidget({ avatarUrl: '' })}
                            disabled={avatarUploading}
                            sx={{ borderRadius: 2 }}
                          >
                            Remove
                          </Button>
                        )}
                      </Stack>

                      <Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }} gap={1.5}>
                        <TextField
                          size="small"
                          fullWidth
                          label="Avatar text (initials)"
                          value={envForm.chatWidget.avatarText}
                          onChange={(e) => updateChatWidget({ avatarText: e.target.value })}
                          placeholder="VB"
                          helperText="Fallback initials when no image is present"
                        />
                        <TextField
                          size="small"
                          fullWidth
                          label="Avatar image URL"
                          value={envForm.chatWidget.avatarUrl}
                          onChange={(e) => updateChatWidget({ avatarUrl: e.target.value })}
                          placeholder="https://example.com/avatar.png"
                          helperText="Direct image URL or auto-filled on upload"
                        />
                      </Box>
                    </Box>
                  </Box>
                  <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 3, bgcolor: 'action.hover' }}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.75 }}>
                      Header visibility
                    </Typography>
                    <FormControlLabel
                      control={<Checkbox checked={Boolean(envForm.chatWidget.showHeader)} onChange={(e) => updateChatWidget({ showHeader: e.target.checked })} />}
                      label="Show complete header"
                    />
                    <Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }}>
                      {[
                        ['showAvatar', 'Avatar'],
                        ['showTitle', 'Title'],
                        ['showSubtitle', 'Subtitle'],
                        ['showStatus', 'Bot name • status'],
                        ['showReset', 'Reset button'],
                      ].map(([key, label]) => (
                        <FormControlLabel
                          key={key}
                          disabled={!envForm.chatWidget.showHeader}
                          control={<Checkbox checked={Boolean(envForm.chatWidget[key])} onChange={(e) => updateChatWidget({ [key]: e.target.checked })} />}
                          label={label}
                        />
                      ))}
                    </Box>
                    <FormControlLabel
                      control={<Checkbox checked={Boolean(envForm.chatWidget.showHome)} onChange={(e) => updateChatWidget({ showHome: e.target.checked })} />}
                      label="Show home button"
                    />
                    <Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }} gap={1.5} sx={{ mt: 1 }}>
                      <TextField
                        fullWidth
                        label="Status text"
                        value={envForm.chatWidget.statusText}
                        disabled={!envForm.chatWidget.showHeader || !envForm.chatWidget.showStatus}
                        onChange={(e) => updateChatWidget({ statusText: e.target.value })}
                        placeholder="Online"
                      />
                      <TextField
                        fullWidth
                        label="Reset button label"
                        value={envForm.chatWidget.resetLabel}
                        disabled={!envForm.chatWidget.showHeader || !envForm.chatWidget.showReset}
                        onChange={(e) => updateChatWidget({ resetLabel: e.target.value })}
                        placeholder="Reset"
                      />
                    </Box>
                  </Paper>
                </Box>
              </Paper>

              <Paper variant="outlined" sx={{ ...chatWidgetCardSx }}>
                <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ fontSize: '1rem', mb: 0.75 }}>
                  Messages
                </Typography>
                <Box display="grid" gap={1.5}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Welcome message"
                    value={envForm.chatWidget.welcomeMessage}
                    onChange={(e) => updateChatWidget({ welcomeMessage: e.target.value })}
                  />
                  <TextField
                    fullWidth
                    label="Input placeholder"
                    value={envForm.chatWidget.inputPlaceholder}
                    onChange={(e) => updateChatWidget({ inputPlaceholder: e.target.value })}
                  />
                </Box>
              </Paper>

              <Paper variant="outlined" sx={{ ...chatWidgetCardSx }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 0.75 }}>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ fontSize: '1rem' }}>
                    Branding & Watermark
                  </Typography>
                  {!isAdmin() && (
                    <Chip
                      size="small"
                      label="Super Admin only"
                      color="default"
                      sx={{ height: 22, fontSize: '0.7rem', fontWeight: 600 }}
                    />
                  )}
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Display the "Powered by Zelorich" watermark with an official hyperlink at the bottom of the chat widget.
                </Typography>
                <FormControlLabel
                  disabled={!isAdmin()}
                  control={
                    <Checkbox
                      checked={Boolean(envForm.chatWidget.showWatermark !== false)}
                      onChange={(e) => updateChatWidget({ showWatermark: e.target.checked })}
                    />
                  }
                  label="Show 'Powered by Zelorich' watermark"
                />
                {!isAdmin() && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    🔒 Only Super Admins can disable Zelorich branding.
                  </Typography>
                )}
              </Paper>

              <Paper variant="outlined" sx={{ ...chatWidgetCardSx }}>
                <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ fontSize: '1rem', mb: 0.75 }}>
                  Refresh behavior
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Choose what the widget does when a visitor reloads the page.
                </Typography>
                <TextField
                  select
                  fullWidth
                  label="On page refresh"
                  value={envForm.chatWidget.refreshSessionMode || 'continue'}
                  onChange={(e) => updateChatWidget({ refreshSessionMode: e.target.value })}
                  helperText={
                    envForm.chatWidget.refreshSessionMode === 'new'
                      ? 'Each reload starts a fresh session and bot journey. The visitor keeps the same browser identity.'
                      : 'Reloads restore the session, chat history, and current journey for this browser.'
                  }
                >
                  <MenuItem value="continue">Continue conversation</MenuItem>
                  <MenuItem value="new">New conversation on refresh</MenuItem>
                </TextField>
              </Paper>

              <Paper variant="outlined" sx={{ ...chatWidgetCardSx }}>
                <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ fontSize: '1rem', mb: 0.75 }}>
                  Colors & theme
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Theme aur colors ko live preview ke saath tune karo.
                </Typography>
                <Box display="grid" gap={1.5}>
                  <Box display="flex" gap={1.25} flexWrap="wrap">
                    {[
                      { key: 'light', label: 'Light theme' },
                      { key: 'dark', label: 'Dark theme' },
                    ].map((option) => {
                      const active = envForm.chatWidget.themeMode === option.key;
                      return (
                        <Paper
                          key={option.key}
                          variant="outlined"
                          onClick={() => updateChatWidget({ themeMode: option.key })}
                          sx={{
                            cursor: 'pointer',
                            px: 1.5,
                            py: 1,
                            minWidth: 128,
                            borderRadius: 2.5,
                            borderColor: active ? 'primary.main' : 'divider',
                            boxShadow: active ? '0 0 0 3px rgba(99,102,241,0.14)' : 'none',
                          }}
                        >
                          <Box display="flex" alignItems="center" gap={1.25}>
                            <Box
                              sx={{
                                width: 18,
                                height: 18,
                                borderRadius: '50%',
                                border: '2px solid',
                                borderColor: active ? 'primary.main' : 'text.disabled',
                                boxShadow: active ? 'inset 0 0 0 4px currentColor' : 'none',
                                color: 'primary.main',
                              }}
                            />
                            <Typography fontWeight={600}>{option.label}</Typography>
                          </Box>
                        </Paper>
                      );
                    })}
                  </Box>

                  <FormControl fullWidth>
                    <InputLabel>Brand color</InputLabel>
                    <Select
                      value={envForm.chatWidget.brandColorMode}
                      label="Brand color"
                      onChange={(e) => updateChatWidget({ brandColorMode: e.target.value })}
                    >
                      <MenuItem value="gradient">Gradient</MenuItem>
                      <MenuItem value="solid">Solid</MenuItem>
                    </Select>
                  </FormControl>

                  <Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }} gap={1.5}>
                    {[
                      { key: 'primaryColor', label: 'Color 1' },
                      { key: 'secondaryColor', label: 'Color 2' },
                      { key: 'complementaryColor', label: 'Complementary color' },
                      { key: 'accentColor', label: 'Accent color' },
                      { key: 'textColor', label: 'Message text color' },
                    ].map((item) => (
                      <Box key={item.key}>
                        <Typography variant="subtitle2" sx={{ mb: 0.75, fontWeight: 700 }}>
                          {item.label}
                        </Typography>
                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: '1fr auto',
                            overflow: 'hidden',
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 2.25,
                            backgroundColor: 'background.paper',
                          }}
                        >
                          <Box
                            sx={{
                              height: 48,
                              position: 'relative',
                              background: envForm.chatWidget[item.key],
                            }}
                          >
                            <input
                              type="color"
                              value={envForm.chatWidget[item.key]}
                              onChange={(e) => updateChatWidget({ [item.key]: e.target.value })}
                              style={{
                                position: 'absolute',
                                inset: 0,
                                width: '100%',
                                height: '100%',
                                opacity: 0,
                                cursor: 'pointer',
                              }}
                            />
                          </Box>
                          <Box sx={{ p: 0.9, display: 'flex', alignItems: 'center', minWidth: 116 }}>
                            <TextField
                              fullWidth
                              variant="standard"
                              value={envForm.chatWidget[item.key]}
                              onChange={(e) => updateChatWidget({ [item.key]: e.target.value })}
                              placeholder="#000000"
                              InputProps={{ disableUnderline: true }}
                            />
                          </Box>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Paper>

              <Paper variant="outlined" sx={{ ...chatWidgetCardSx }}>
                <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ fontSize: '1rem', mb: 0.75 }}>
                  Font
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Widget ke text ka style aur size control karo.
                </Typography>
                <Box display="grid" gap={1.5}>
                  <Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }} gap={1.25}>
                    <FormControl fullWidth>
                      <InputLabel>Font style</InputLabel>
                      <Select
                        value={envForm.chatWidget.fontStyle}
                        label="Font style"
                        onChange={(e) => updateChatWidget({ fontStyle: e.target.value })}
                      >
                        <MenuItem value="default">Default</MenuItem>
                        <MenuItem value="custom">Custom</MenuItem>
                      </Select>
                    </FormControl>
                    <TextField
                      fullWidth
                      type="number"
                      label="Message text size (px)"
                      value={envForm.chatWidget.textSize}
                      onChange={(e) => updateChatWidget({ textSize: e.target.value })}
                      inputProps={{ min: 12, max: 22, step: 1 }}
                      helperText="12px se 22px"
                    />
                  </Box>

                  <Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }} gap={1.25}>
                    <FormControl fullWidth>
                      <InputLabel>Font family</InputLabel>
                      <Select
                        value={envForm.chatWidget.fontFamily}
                        label="Font family"
                        onChange={(e) => updateChatWidget({ fontFamily: e.target.value })}
                      >
                        {!WIDGET_FONT_OPTIONS.some((option) => option.value === envForm.chatWidget.fontFamily) && (
                          <MenuItem value={envForm.chatWidget.fontFamily}>{envForm.chatWidget.fontFamily}</MenuItem>
                        )}
                        {WIDGET_FONT_OPTIONS.map((option) => (
                          <MenuItem key={option.label} value={option.value} sx={{ fontFamily: option.value }}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <TextField
                      fullWidth
                      label="Font URL"
                      value={envForm.chatWidget.fontUrl}
                      onChange={(e) => updateChatWidget({ fontUrl: e.target.value })}
                      placeholder="https://fonts.googleapis.com/css2?family=Mulish"
                    />
                  </Box>

                  <Alert severity="info" sx={{ borderRadius: 3 }}>
                    Custom font ka preview browser me dikh jayega, lekin production widget me wahi font load hoga jo actual website allow kare.
                  </Alert>
                </Box>
              </Paper>

              <Paper variant="outlined" sx={{ ...chatWidgetCardSx }}>
                <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ fontSize: '1rem', mb: 0.75 }}>
                  Size & placement
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Widget kitna bada ho aur site par kahan open ho, yahin se choose karo.
                </Typography>
                <Box display="grid" gap={1.5}>
                  <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr 1fr' }} gap={1.25}>
                    {widgetSizeCards.map((card) => {
                      const active = envForm.chatWidget.size === card.key;
                      return (
                        <Paper
                          key={card.key}
                          variant="outlined"
                          onClick={() => updateChatWidget({
                            size: card.key,
                            width: card.width,
                            height: card.height,
                          })}
                          sx={{
                            cursor: 'pointer',
                            p: 1.1,
                            borderRadius: 2.25,
                            borderColor: active ? 'primary.main' : 'divider',
                            boxShadow: active ? '0 0 0 3px rgba(99,102,241,0.14)' : 'none',
                          }}
                        >
                          <Box
                            sx={{
                              height: 72,
                              borderRadius: 2,
                              border: '1px solid',
                              borderColor: 'divider',
                              background: 'linear-gradient(180deg, rgba(241,245,249,0.95) 0%, rgba(248,250,252,0.75) 100%)',
                              position: 'relative',
                              overflow: 'hidden',
                              mb: 1,
                            }}
                          >
                            <Box
                              sx={{
                                width: `${card.scale * 72}px`,
                                height: `${card.scale * 46}px`,
                                borderRadius: 1.5,
                                background: 'linear-gradient(180deg, #ffffff 0%, #eff6ff 100%)',
                                border: '1px solid #cbd5e1',
                                position: 'absolute',
                                right: 12,
                                bottom: 12,
                              }}
                            />
                          </Box>
                          <Typography fontWeight={700}>{card.label}</Typography>
                        </Paper>
                      );
                    })}
                  </Box>

                  <Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }} gap={1.5}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Widget width (px)"
                      value={envForm.chatWidget.width}
                      onChange={(e) => updateChatWidget({ width: e.target.value })}
                      inputProps={{ min: 320, max: 720, step: 1 }}
                      helperText="320px se 720px"
                    />
                    <TextField
                      fullWidth
                      type="number"
                      label="Widget height (px)"
                      value={envForm.chatWidget.height}
                      onChange={(e) => updateChatWidget({ height: e.target.value })}
                      inputProps={{ min: 420, max: 900, step: 1 }}
                      helperText="420px se 900px"
                    />
                  </Box>

                  <Alert severity="info" sx={{ borderRadius: 3 }}>
                    Published bot isi saved width aur height ko use karega; browser viewport chhota ho to widget screen ke andar fit hoga.
                  </Alert>

                  <Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }} gap={1.5}>
                    <FormControl fullWidth>
                      <InputLabel>Position</InputLabel>
                      <Select
                        value={envForm.chatWidget.position}
                        label="Position"
                        onChange={(e) => updateChatWidget({ position: e.target.value })}
                      >
                        <MenuItem value="right">Right</MenuItem>
                        <MenuItem value="left">Left</MenuItem>
                      </Select>
                    </FormControl>
                    <FormControl fullWidth>
                      <InputLabel>Initial state</InputLabel>
                      <Select
                        value={envForm.chatWidget.initialState}
                        label="Initial state"
                        onChange={(e) => updateChatWidget({ initialState: e.target.value })}
                      >
                        <MenuItem value="expanded">Expanded</MenuItem>
                        <MenuItem value="minimized">Minimized</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                  <Box sx={{ mt: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                    <Typography variant="subtitle1" fontWeight={700}>Minimized chat invitation</Typography>
                    <FormControlLabel control={<Checkbox checked={Boolean(envForm.chatWidget.launcherMessageEnabled)} onChange={(event) => updateChatWidget({ launcherMessageEnabled: event.target.checked })} />} label="Show a message above the chat launcher" />
                    <TextField fullWidth size="small" label="Invitation message" value={envForm.chatWidget.launcherMessage || ''} onChange={(event) => updateChatWidget({ launcherMessage: event.target.value })} inputProps={{ maxLength: 200 }} placeholder="👋 Need help? Chat with us!" helperText="Shown only while minimized. Visitors can open chat or dismiss it for this tab session. Up to 200 characters." />
                    {envForm.chatWidget.launcherMessageEnabled && envForm.chatWidget.launcherMessage?.trim() && <Box sx={{ mt: 1.5, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: 'background.paper', maxWidth: 300, overflowWrap: 'anywhere' }}>{envForm.chatWidget.launcherMessage}<Typography variant="caption" display="block" color="text.secondary">Launcher message preview</Typography></Box>}
                  </Box>
                </Box>
              </Paper>

              <Box sx={{ mt: 0.5 }}>
                <Button variant="contained" onClick={handleSaveEnvironmentConfig} disabled={envSaving}>
                  {envSaving ? <CircularProgress size={20} /> : 'Save Chat Widget'}
                </Button>
              </Box>
            </Box>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
                alignSelf: 'start',
                position: { xs: 'static', lg: 'sticky' },
                top: { xs: 0, lg: 80 },
                zIndex: 10,
              }}
            >
              <Paper
                variant="outlined"
                sx={{
                  ...previewCardSx,
                  overflow: 'hidden',
                  p: 2,
                  boxShadow: '0 20px 45px -15px rgba(15, 23, 42, 0.12)',
                  borderColor: 'primary.light',
                  borderWidth: 1,
                }}
              >
                <Box display="flex" alignItems="center" justifyContent="space-between" pb={1} borderBottom="1px solid" borderColor="divider">
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: 'success.main',
                        boxShadow: '0 0 0 3px rgba(34, 197, 94, 0.25)',
                      }}
                    />
                    <Typography variant="subtitle1" fontWeight={700} sx={{ fontSize: '0.95rem' }}>
                      Live Widget Preview
                    </Typography>
                  </Box>
                  <Chip
                    label="Real-time"
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600 }}
                  />
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: -0.5 }}>
                  Colors, fonts, and layout updates reflect instantly below
                </Typography>
                <Box
                  sx={{
                    width: '100%',
                    maxHeight: 'calc(100vh - 210px)',
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    p: 0.5,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'flex-start',
                  }}
                >
                  <Box
                    sx={{
                      width: resolvedChatWidgetPreview.width,
                      height: resolvedChatWidgetPreview.height,
                      flex: '0 0 auto',
                      mx: resolvedChatWidgetPreview.position === 'left' ? 0 : 'auto',
                    }}
                  >
                    <ChatPreview
                      title={resolvedChatWidgetPreview.title}
                      subtitle={resolvedChatWidgetPreview.subtitle}
                      botName={resolvedChatWidgetPreview.title}
                      avatarText={resolvedChatWidgetPreview.avatarText}
                      avatarUrl={resolvedChatWidgetPreview.avatarUrl}
                      status={resolvedChatWidgetPreview.statusText}
                      agentIdentity={resolveAgentIdentityText(resolvedChatWidgetPreview.agentIdentityMode, resolvedChatWidgetPreview.agentIdentityLabel, 'Lucky')}
                      showHeader={resolvedChatWidgetPreview.showHeader}
                      showAvatar={resolvedChatWidgetPreview.showAvatar}
                      showTitle={resolvedChatWidgetPreview.showTitle}
                      showSubtitle={resolvedChatWidgetPreview.showSubtitle}
                      showStatus={resolvedChatWidgetPreview.showStatus}
                      showReset={resolvedChatWidgetPreview.showReset}
                      showHome={resolvedChatWidgetPreview.showHome}
                      showWatermark={resolvedChatWidgetPreview.showWatermark}
                      resetLabel={resolvedChatWidgetPreview.resetLabel}
                      messages={[{ role: 'bot', text: resolvedChatWidgetPreview.welcomeMessage }]}
                      inputValue=""
                      onInputChange={() => {}}
                      onSend={() => {}}
                      onHome={() => {}}
                      onReset={() => {}}
                      placeholder={resolvedChatWidgetPreview.inputPlaceholder}
                      footerHint={`Widget ${resolvedChatWidgetPreview.position} • ${resolvedChatWidgetPreview.initialState} • ${resolvedChatWidgetPreview.refreshSessionMode === 'new' ? 'new session on refresh' : 'continue session'} • ${resolvedChatWidgetPreview.width}×${resolvedChatWidgetPreview.height}px`}
                      themeMode={resolvedChatWidgetPreview.themeMode}
                      primaryColor={resolvedChatWidgetPreview.primaryColor}
                      accentColor={resolvedChatWidgetPreview.brandColorMode === 'gradient' ? resolvedChatWidgetPreview.secondaryColor : resolvedChatWidgetPreview.accentColor}
                      complementaryColor={resolvedChatWidgetPreview.complementaryColor}
                      fontFamily={resolvedChatWidgetPreview.fontFamily}
                      fontSize={resolvedChatWidgetPreview.fontSize}
                      textSize={resolvedChatWidgetPreview.textSize}
                      textColor={resolvedChatWidgetPreview.textColor}
                    />
                  </Box>
                </Box>
              </Paper>
            </Box>
          </Box>
        </Paper>
      )}
      {settingsTab === 4 && selectedBot && (
        <Paper sx={{ p: 3, mt: 2 }}>
          <Typography variant="h6" gutterBottom>
            Publish your bot
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
            Embed this bot as a web widget on your website. Copy the script below and paste it before the closing &lt;/body&gt; tag of your HTML page.
          </Typography>
          
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>Bot:</strong> {selectedBot} | <strong>Environment:</strong> {selectedEnv}
            </Typography>
          </Alert>

          {(isAdmin() || isBotAdmin(selectedBot)) && (
            <Paper variant="outlined" sx={{ p: 2, mb: 3, borderColor: 'primary.main' }}>
              <Typography variant="subtitle1" fontWeight={800}>Deploy DEV to PROD</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Release Center deploys flows, configuration, bot languages, and localization together as one versioned release.
              </Typography>
              <Box display="flex" gap={2} flexWrap="wrap">
                <Button
                  variant="contained"
                  startIcon={<PublishIcon />}
                  onClick={() => { window.location.href = '/deployments'; }}
                >
                  Open Release Center
                </Button>
              </Box>
            </Paper>
          )}
          
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2" fontWeight="bold">
                Embed Script
              </Typography>
              <Button
                size="small"
                startIcon={<ContentCopyIcon />}
                onClick={() => {
                  const script = generateWidgetScript(selectedBot, selectedEnv);
                  navigator.clipboard.writeText(script);
                  setMessage({ type: 'success', text: 'Script copied to clipboard!' });
                  setTimeout(() => setMessage(null), 3000);
                }}
              >
                Copy Script
              </Button>
            </Box>
            <TextField
              fullWidth
              multiline
              rows={12}
              value={generateWidgetScript(selectedBot, selectedEnv)}
              InputProps={{
                readOnly: true,
                sx: {
                  fontFamily: 'monospace',
                  fontSize: '0.875rem',
                },
              }}
              sx={{
                '& .MuiInputBase-root': {
                  bgcolor: '#f5f5f5',
                },
              }}
            />
          </Box>
          
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2" component="div">
              <strong>Important Notes:</strong>
              <ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: 20 }}>
                <li>If using <code>localhost</code>, the widget will only work on your local machine. For external websites, use a publicly accessible backend URL.</li>
                <li>For production, set <code>REACT_APP_API_URL</code> in your .env file to your backend server URL (e.g., <code>https://api.yourdomain.com</code>).</li>
                <li>Make sure your bot is configured with the correct environment settings before publishing.</li>
                <li>For testing on external sites, use a tunneling service like ngrok or deploy your backend to a public server.</li>
              </ul>
            </Typography>
          </Alert>
          
          {selectedBot && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Current Backend URL:</strong> {generateWidgetScript(selectedBot, selectedEnv).match(/apiUrl: '([^']+)'/)?.[1] || 'Not set'}
                {generateWidgetScript(selectedBot, selectedEnv).match(/apiUrl: '([^']+)'/)?.[1]?.includes('localhost') && (
                  <span style={{ color: '#d32f2f', fontWeight: 'bold' }}> ⚠️ This won't work on external websites!</span>
                )}
              </Typography>
            </Alert>
          )}
        </Paper>
      )}
      
      {settingsTab === accessControlTabIndex && selectedBot && (
        <Paper sx={{ p: 3, mt: 2 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} flexWrap="wrap" mb={2}>
            <Box>
              <Typography variant="h6">
                Access Control
              </Typography>
              <Typography variant="body2" color="textSecondary">
                View everyone who has access to <strong>{selectedBot}</strong>, their email IDs, workspace roles, and invite new members directly for this bot.
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<PersonAddIcon />}
              onClick={() => {
                setInviteScope('access-control');
                setInviteForm((prev) => ({
                  ...prev,
                  botIds: selectedBot ? [selectedBot] : [],
                }));
                setInviteDialogOpen(true);
              }}
            >
              Invite Access
            </Button>
          </Box>

          {accessLoading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ display: 'grid', gap: 1.5 }}>
              {accessMembers.length === 0 ? (
                <Alert severity="info">
                  No users have access to this bot yet, or you do not have bot-admin rights for this workspace.
                </Alert>
              ) : (
                accessMembers.map((member) => (
                  <Paper
                    key={`${member.userId}-${member.email}`}
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 1.5,
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Box>
                      <Typography variant="subtitle1" fontWeight={700}>
                        {member.name || member.email}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {member.email}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end', alignItems: 'center' }}>
                        {(Array.isArray(member.workspaceRoles) && member.workspaceRoles.length > 0
                          ? member.workspaceRoles
                          : [member.workspaceRole || 'developer']
                        ).map((role) => (
                          <Chip
                            key={role}
                            label={String(role).toUpperCase()}
                            color={role === 'admin' ? 'primary' : role === 'agent' ? 'secondary' : 'default'}
                            size="small"
                          />
                        ))}
                        <Chip label={`Env: ${(member.environmentsAllowed || []).join(', ') || 'dev'}`} variant="outlined" size="small" />
                        <Chip label={member.status || 'active'} variant="outlined" size="small" />
                      </Box>
                      <IconButton
                        size="small"
                        aria-label={`Actions for ${member.name || member.email}`}
                        onClick={(e) => handleOpenActionMenu(e, member)}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Paper>
                ))
              )}
            </Box>
          )}

          <Menu
            anchorEl={actionAnchorEl}
            open={Boolean(actionAnchorEl)}
            onClose={handleCloseActionMenu}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <MenuItem onClick={() => handleOpenEditRole(activeActionMember)}>
              <ListItemIcon>
                <EditIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Edit Role" />
            </MenuItem>
            <MenuItem onClick={() => handleOpenDeleteAccess(activeActionMember)} sx={{ color: 'error.main' }}>
              <ListItemIcon sx={{ color: 'error.main' }}>
                <DeleteOutlineIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Delete Access" />
            </MenuItem>
          </Menu>
        </Paper>
      )}

      {settingsTab === teamTabIndex && isAdmin() && (
        <Paper sx={{ p: 3, mt: 2 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">
              Invite Team Member
            </Typography>
            <Button
              variant="contained"
              startIcon={<PersonAddIcon />}
              onClick={() => {
                setInviteScope('team');
                setInviteDialogOpen(true);
              }}
            >
              Invite by Email
            </Button>
          </Box>
          <Typography variant="body2" color="textSecondary">
            Invite team members by email. They will receive login credentials and can access assigned bots.
          </Typography>
        </Paper>
      )}

      {/* Invite Member Dialog */}
      <Dialog open={inviteDialogOpen} onClose={() => { setInviteDialogOpen(false); setShowInvitePassword(false); }} maxWidth="sm" fullWidth>
        <DialogTitle>{inviteScope === 'access-control' ? 'Invite Bot Access' : 'Invite Team Member'}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Name"
            value={inviteForm.name}
            onChange={(e) => setInviteForm((prev) => ({ ...prev, name: e.target.value }))}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={inviteForm.email}
            onChange={(e) => setInviteForm((prev) => ({ ...prev, email: e.target.value }))}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Password (optional)"
            type={showInvitePassword ? 'text' : 'password'}
            value={inviteForm.password || ''}
            onChange={(e) => setInviteForm((prev) => ({ ...prev, password: e.target.value }))}
            margin="normal"
            placeholder="Leave blank to auto-generate random password"
            helperText={
              inviteForm.password && inviteForm.password.trim().length > 0 && inviteForm.password.trim().length < 6
                ? 'Password must be at least 6 characters'
                : 'Custom password (min 6 characters), or leave blank for auto-generated'
            }
            error={Boolean(inviteForm.password && inviteForm.password.trim().length > 0 && inviteForm.password.trim().length < 6)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowInvitePassword(!showInvitePassword)}
                    edge="end"
                    size="small"
                  >
                    {showInvitePassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel id="workspace-roles-select-label">Workspace Roles</InputLabel>
            <Select
              labelId="workspace-roles-select-label"
              multiple
              value={inviteForm.workspaceRoles || [inviteForm.workspaceRole || 'developer']}
              label="Workspace Roles"
              onChange={(e) => {
                const value = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
                const selected = Array.isArray(value) && value.length > 0 ? value : ['developer'];
                setInviteForm((prev) => ({
                  ...prev,
                  workspaceRoles: selected,
                  workspaceRole: selected.includes('admin') ? 'admin' : selected[0],
                }));
              }}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((val) => {
                    const roleLabels = {
                      admin: 'Admin',
                      developer: 'Developer',
                      agent_admin: 'Agent Admin',
                      agent: 'Agent',
                    };
                    return (
                      <Chip
                        key={val}
                        label={roleLabels[val] || val}
                        size="small"
                        color={val === 'admin' ? 'primary' : val === 'agent' ? 'secondary' : 'default'}
                      />
                    );
                  })}
                </Box>
              )}
            >
              <MenuItem value="admin">
                <Checkbox checked={(inviteForm.workspaceRoles || [inviteForm.workspaceRole]).includes('admin')} />
                <ListItemText primary="Admin" secondary="Full workspace administration & configuration" />
              </MenuItem>
              <MenuItem value="developer">
                <Checkbox checked={(inviteForm.workspaceRoles || [inviteForm.workspaceRole]).includes('developer')} />
                <ListItemText primary="Developer" secondary="Bot flow editing and variable management" />
              </MenuItem>
              <MenuItem value="agent_admin">
                <Checkbox checked={(inviteForm.workspaceRoles || [inviteForm.workspaceRole]).includes('agent_admin')} />
                <ListItemText primary="Agent Admin" secondary="Agent desk administration and queues" />
              </MenuItem>
              <MenuItem value="agent">
                <Checkbox checked={(inviteForm.workspaceRoles || [inviteForm.workspaceRole]).includes('agent')} />
                <ListItemText primary="Agent" secondary="Handle live conversations in Agent Desk" />
              </MenuItem>
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary">
            Select one or more roles (e.g. Admin + Developer). Users holding Admin privileges retain full admin access across all workspace features.
          </Typography>
          {inviteScope === 'access-control' ? (
            <TextField
              fullWidth
              margin="normal"
              label="Bot"
              value={selectedBot || ''}
              InputProps={{ readOnly: true }}
            />
          ) : (
            <FormControl fullWidth margin="normal">
              <InputLabel>Assign Bots (Optional)</InputLabel>
              <Select
                multiple
                value={inviteForm.botIds}
                onChange={(e) => setInviteForm((prev) => ({ ...prev, botIds: e.target.value }))}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((botId) => {
                      /** Purpose: Resolve the bot associated with an identifier. Why: Settings navigation must retain the correct selected bot. */
                      const bot = bots.find(b => b.botId === botId);
                      return <Chip key={botId} label={bot?.botName || botId} size="small" />;
                    })}
                  </Box>
                )}
              >
                {bots.map((bot, index) => {
                  const id = bot.botId || bot._id || `bot-${index}`;
                  return (
                    <MenuItem key={id} value={id}>
                      {bot.botName || id}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          )}
          <Box mt={2}>
            <Typography variant="subtitle2" gutterBottom>
              Allow Environments:
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={inviteForm.environmentsAllowed.includes('dev')}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setInviteForm((prev) => ({
                        ...prev,
                        environmentsAllowed: [...prev.environmentsAllowed, 'dev'],
                      }));
                    } else {
                      setInviteForm((prev) => ({
                        ...prev,
                        environmentsAllowed: prev.environmentsAllowed.filter(e => e !== 'dev'),
                      }));
                    }
                  }}
                />
              }
              label="Development"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={inviteForm.environmentsAllowed.includes('prod')}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setInviteForm((prev) => ({
                        ...prev,
                        environmentsAllowed: [...prev.environmentsAllowed, 'prod'],
                      }));
                    } else {
                      setInviteForm((prev) => ({
                        ...prev,
                        environmentsAllowed: prev.environmentsAllowed.filter(e => e !== 'prod'),
                      }));
                    }
                  }}
                />
              }
              label="Production"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setInviteDialogOpen(false); setShowInvitePassword(false); }}>Cancel</Button>
          <Button
            onClick={handleInviteMember}
            variant="contained"
            disabled={inviting || !inviteForm.email || !inviteForm.name || Boolean(inviteForm.password && inviteForm.password.trim().length > 0 && inviteForm.password.trim().length < 6)}
          >
            {inviting ? <CircularProgress size={24} /> : 'Send Invitation'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Role Dialog */}
      <Dialog
        open={editRoleDialogOpen}
        onClose={() => !editRoleSaving && setEditRoleDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Workspace Roles</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2, mt: 1, p: 2, borderRadius: 2.5, bgcolor: 'action.hover' }}>
            <Typography variant="subtitle1" fontWeight={700}>
              {editRoleMember?.name || editRoleMember?.email}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {editRoleMember?.email}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              Bot: <strong>{selectedBot}</strong>
            </Typography>
          </Box>

          <FormControl fullWidth margin="normal">
            <InputLabel id="edit-workspace-roles-select-label">Workspace Roles</InputLabel>
            <Select
              labelId="edit-workspace-roles-select-label"
              multiple
              value={editRoleValues}
              label="Workspace Roles"
              onChange={(e) => {
                const value = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
                const selected = Array.isArray(value) && value.length > 0 ? value : ['developer'];
                setEditRoleValues(selected);
              }}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((val) => {
                    const roleLabels = {
                      admin: 'Admin',
                      developer: 'Developer',
                      agent_admin: 'Agent Admin',
                      agent: 'Agent',
                    };
                    return (
                      <Chip
                        key={val}
                        label={roleLabels[val] || val}
                        size="small"
                        color={val === 'admin' ? 'primary' : val === 'agent' ? 'secondary' : 'default'}
                      />
                    );
                  })}
                </Box>
              )}
            >
              <MenuItem value="admin">
                <Checkbox checked={editRoleValues.includes('admin')} />
                <ListItemText primary="Admin" secondary="Full workspace administration & configuration" />
              </MenuItem>
              <MenuItem value="developer">
                <Checkbox checked={editRoleValues.includes('developer')} />
                <ListItemText primary="Developer" secondary="Bot flow editing and variable management" />
              </MenuItem>
              <MenuItem value="agent_admin">
                <Checkbox checked={editRoleValues.includes('agent_admin')} />
                <ListItemText primary="Agent Admin" secondary="Agent desk administration and queues" />
              </MenuItem>
              <MenuItem value="agent">
                <Checkbox checked={editRoleValues.includes('agent')} />
                <ListItemText primary="Agent" secondary="Handle live conversations in Agent Desk" />
              </MenuItem>
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            Assign one or more roles. Users with Admin privileges have full access across all workspace tabs and features.
          </Typography>

          <Box mt={1}>
            <Typography variant="subtitle2" gutterBottom>
              Allow Environments:
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={editRoleEnvs.includes('dev')}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setEditRoleEnvs((prev) => [...prev, 'dev']);
                    } else {
                      setEditRoleEnvs((prev) => prev.filter((env) => env !== 'dev'));
                    }
                  }}
                />
              }
              label="Development (DEV)"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={editRoleEnvs.includes('prod')}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setEditRoleEnvs((prev) => [...prev, 'prod']);
                    } else {
                      setEditRoleEnvs((prev) => prev.filter((env) => env !== 'prod'));
                    }
                  }}
                />
              }
              label="Production (PROD)"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditRoleDialogOpen(false)} disabled={editRoleSaving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSaveEditRole} disabled={editRoleSaving}>
            {editRoleSaving ? <CircularProgress size={20} /> : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Access Confirmation Dialog */}
      <Dialog
        open={deleteAccessDialogOpen}
        onClose={() => !deleteAccessSaving && setDeleteAccessDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ color: 'error.main' }}>
          Revoke Bot Access
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Are you sure you want to remove access for <strong>{deleteAccessMember?.name || deleteAccessMember?.email}</strong> ({deleteAccessMember?.email}) from bot <strong>{selectedBot}</strong>?
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            This user will immediately lose access to this bot's flows, configuration, and agent desk.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteAccessDialogOpen(false)} disabled={deleteAccessSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDeleteAccess}
            disabled={deleteAccessSaving}
          >
            {deleteAccessSaving ? <CircularProgress size={20} color="inherit" /> : 'Delete Access'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
